/**
 * api.mock.ts — the demo adapter: the exact dataset of `docs/spec-business.md` §7, served
 * through the same `Api` interface as `api.ts` so every screen renders before the business
 * server exists (ARCHITECTURE §9: "The web team builds against `api-types.ts` with a mock
 * adapter until the real server is up, then switches by env").
 *
 * Every figure below is DEMO data (spec §7, anchored at 6.9.2026 06:12). It is never presented
 * as the owner's real money: `isMock` is true, which lights the "נתוני הדגמה" chip in the top
 * bar and the demo pill on the login screen.
 *
 * Money is integer agorot on the wire, exactly like the server (ARCHITECTURE §9).
 */

import { buildBusinessNarrative, buildUrgency } from '../../server/insight.ts';
import type { InsightInput } from '../../server/insight.ts';
import type {
  Account,
  AccountRef,
  AgingBucket,
  AgingBucketStat,
  Alert,
  AlertFilter,
  AlertsQuery,
  AlertsResponse,
  AlertType,
  BalancePoint,
  CashflowMonth,
  CashflowResponse,
  Connection,
  Customer,
  CustomerBalance,
  CustomersResponse,
  DayString,
  DocType,
  ExpenseCategory,
  ExpenseCategorySettings,
  ExpenseCategoryStat,
  ExpenseMonth,
  ExpenseRef,
  ExpenseRow,
  ExpensesQuery,
  ExpensesResponse,
  ForecastEvent,
  ForecastLine,
  ForecastResponse,
  Freshness,
  Invoice,
  InvoiceDetail,
  InvoiceStatus,
  InvoicesQuery,
  InvoicesResponse,
  MonthString,
  MrrPoint,
  OpsCallsQuery,
  OpsCallsResponse,
  OpsCustomersResponse,
  OpsSubscriptionsResponse,
  OpsSummary,
  PaymentChannel,
  PaymentMethod,
  Period,
  PeriodParam,
  ReminderDraft,
  ServiceCall,
  ServiceLine,
  ServiceLineMonth,
  SettingsResponse,
  SourceFreshness,
  Subscription,
  Supplier,
  SuppliersResponse,
  SummaryResponse,
  SyncRun,
  SyncStatusResponse,
  TaxEvent,
  TaxSettings,
  TestIntegrationResponse,
  TransactionDetail,
  TransactionRow,
  TransactionsQuery,
  TransactionsResponse,
  User,
  VatPeriod,
  VatPeriodRef,
  VatResponse,
  VatTrendPoint,
} from '../../api-types.ts';
import type { Api } from './api.ts';
import { ApiError } from './api.ts';
import { addDays, addMonths, daysBetween, daysInMonth, monthEnd, monthOf, monthStart, parts } from './dates.ts';
import { he, CATEGORY, DOC_TYPE } from './i18n/he.ts';
import { fmtDate, fmtMoney, monthShort } from './format.ts';
import { loansFromAccounts } from './loans.ts';

const ILS = (shekels: number): number => Math.round(shekels * 100);

const TODAY: DayString = '2026-09-06';
const LAST_FETCH = '2026-09-06T06:12:00.000+03:00';
const SERVER_TIME = '2026-09-06T09:24:00.000+03:00';
const VAT_RATE = 0.18;
const HISTORY_FROM: DayString = '2025-09-01';

// ───────────────────────────── accounts & connection ─────────────────────────────

const CHECKING_REF: AccountRef = {
  id: 'acc-checking-6751',
  type: 'CHECKING',
  providerId: 'discount',
  connectionId: 'conn-discount',
  name: 'עו"ש עסקי',
  numberLast4: '6751',
  creditStatus: null,
  connectionStatus: 'ACTIVE',
  dataAsOf: LAST_FETCH,
};

const CARD_REF: AccountRef = {
  id: 'acc-card-7712',
  type: 'CARD',
  providerId: 'cal',
  connectionId: 'conn-discount',
  name: 'Cal עסקי (דמו)',
  numberLast4: '7712',
  creditStatus: 'enabled',
  connectionStatus: 'ACTIVE',
  dataAsOf: LAST_FETCH,
};

const AVAILABLE = ILS(38420);

/**
 * Demo loans (REDESIGN §6). Shaped like the live data the screen has to survive: Discount bank
 * loans (one fixed, one prime-linked), a Cal facility that is `קרדיט בכרטיס` and never mixed with
 * the bank loans, one loan assigned to the PRIVATE system so the `הכל · פרטי · עסקי` control has
 * something on both sides, and one facility whose contract already ended — the live snapshot has
 * 19 of those, and they belong in the collapsed "הלוואות שנסגרו" group, not in the totals.
 */
function loanAccount(
  id: string,
  name: string,
  providerId: string,
  options: {
    balance: number;
    rate: number;
    interestType: 'FIXD' | 'INDE';
    index?: string;
    start: DayString;
    end: DayString;
    assigned: boolean;
    loanType: 'RETAIL_LOAN' | 'CREDIT_CARD';
    product: string;
    last4: string;
  },
): Account {
  return {
    id,
    type: 'LOAN',
    providerId,
    connectionId: providerId === 'cal' ? 'conn-discount' : 'conn-discount',
    name,
    numberLast4: options.last4,
    creditStatus: null,
    connectionStatus: 'ACTIVE',
    dataAsOf: LAST_FETCH,
    currency: 'ILS',
    product: options.product,
    displayNumber: null,
    balances: {
      booked: ILS(options.balance),
      bookedInclLimit: null,
      forwardAvailable: null,
      expected: ILS(options.balance),
      interimBooked: null,
      interimAvailable: null,
      referenceDate: TODAY,
    },
    creditLimitAgorot: null,
    overdraftLimitAgorot: null,
    cardDueDay: null,
    loanType: options.loanType,
    contractStart: options.start,
    contractEnd: options.end,
    interest: { type: options.interestType, ratePct: options.rate, relatedIndex: options.index ?? null },
    canonical: true,
    assigned: options.assigned,
    txCount: 0,
    firstSeen: '2025-08-09T00:00:00.000Z',
    updatedAt: LAST_FETCH,
    displayBalanceAgorot: ILS(options.balance),
    displayBalanceKind: 'booked',
  };
}

const LOAN_ACCOUNTS: Account[] = [
  loanAccount('acc-loan-biz-1', 'הלוואה ללקוחות עסקיים', 'discount', {
    balance: 84500,
    rate: 7.4,
    interestType: 'FIXD',
    start: '2024-03-01',
    end: '2028-03-01',
    assigned: true,
    loanType: 'RETAIL_LOAN',
    product: 'הלוואה עסקית',
    last4: '4021',
  }),
  loanAccount('acc-loan-biz-2', 'מתווה בנק ישראל — מסחרי', 'discount', {
    balance: 31200,
    rate: 6.1,
    interestType: 'INDE',
    index: 'prime',
    start: '2025-02-01',
    end: '2027-02-01',
    assigned: true,
    loanType: 'RETAIL_LOAN',
    product: 'הלוואה מסחרית',
    last4: '4188',
  }),
  loanAccount('acc-loan-card-1', 'קרדיט בכרטיס Cal', 'cal', {
    balance: 9640,
    rate: 11.5,
    interestType: 'FIXD',
    start: '2026-01-10',
    end: '2027-05-10',
    assigned: true,
    loanType: 'CREDIT_CARD',
    product: 'קרדיט',
    last4: '7712',
  }),
  loanAccount('acc-loan-private-1', 'רכב בתשלומים', 'discount', {
    balance: 46800,
    rate: 5.9,
    interestType: 'FIXD',
    start: '2024-07-01',
    end: '2029-06-01',
    assigned: false,
    loanType: 'RETAIL_LOAN',
    product: 'הלוואת רכב',
    last4: '3907',
  }),
  loanAccount('acc-loan-card-closed', 'קרדיט בכרטיס Cal (הסתיים)', 'cal', {
    balance: 0,
    rate: 10.9,
    interestType: 'FIXD',
    start: '2025-04-10',
    end: '2026-04-10',
    assigned: true,
    loanType: 'CREDIT_CARD',
    product: 'קרדיט',
    last4: '7712',
  }),
];

const ACCOUNTS: Account[] = [
  {
    ...CHECKING_REF,
    currency: 'ILS',
    product: 'עו"ש עסקי',
    displayNumber: 'בנק 11 · סניף 014 · חשבון ***6751',
    balances: {
      booked: AVAILABLE,
      bookedInclLimit: ILS(58420),
      forwardAvailable: AVAILABLE,
      expected: AVAILABLE,
      interimBooked: null,
      interimAvailable: AVAILABLE,
      referenceDate: TODAY,
    },
    creditLimitAgorot: null,
    overdraftLimitAgorot: ILS(20000),
    cardDueDay: null,
    loanType: null,
    contractStart: null,
    contractEnd: null,
    interest: null,
    canonical: true,
    assigned: true,
    txCount: 1749,
    firstSeen: '2025-08-09T00:00:00.000Z',
    updatedAt: LAST_FETCH,
    displayBalanceAgorot: AVAILABLE,
    displayBalanceKind: 'available',
  },
  {
    ...CARD_REF,
    currency: 'ILS',
    product: 'Cal Business',
    displayNumber: '****7712',
    balances: {
      booked: ILS(2313),
      bookedInclLimit: null,
      forwardAvailable: null,
      expected: null,
      interimBooked: ILS(2313),
      interimAvailable: null,
      referenceDate: TODAY,
    },
    creditLimitAgorot: ILS(25000),
    overdraftLimitAgorot: null,
    cardDueDay: 10,
    loanType: 'CREDIT_CARD',
    contractStart: null,
    contractEnd: null,
    interest: null,
    canonical: true,
    assigned: true,
    txCount: 425,
    firstSeen: '2025-08-09T00:00:00.000Z',
    updatedAt: LAST_FETCH,
    displayBalanceAgorot: ILS(2313),
    displayBalanceKind: 'debt',
  },
  ...LOAN_ACCOUNTS,
];

const CONNECTIONS: Connection[] = [
  {
    id: 'conn-discount',
    providerId: 'discount',
    status: 'ACTIVE',
    mode: 'PERIODIC',
    startDate: '2025-08-09',
    expiryDate: '2027-02-09',
    lastFetchedAt: LAST_FETCH,
    lastFetchedDataDate: TODAY,
    restrictedTo: ['CACC', 'CARD'],
    counts: { accounts: 1, cards: 1, savings: 0, loans: 5, securities: 0, transactions: 2174 },
    accountIds: [CHECKING_REF.id, CARD_REF.id],
    assignedAccountCount: 2,
    maskedPans: ['****7712'],
    needsRelink: false,
  },
];

// ───────────────────────────── 13 months (spec §7.2 / §7.3 / §7.4b) ─────────────────────────────

interface MonthRow {
  ym: MonthString;
  subs: number;
  remote: number;
  field: number;
  /** EQUIPMENT, SOFTWARE, TELECOM, VEHICLE, MARKETING, BOOKKEEPING, CONTRACTORS */
  exp: [number, number, number, number, number, number, number];
  bankIn: number;
  bankOut: number;
  card: number;
  tax: number;
  contractor: number;
  ownerDraw: number;
  endBal: number;
  subsCount: number;
  partial?: boolean;
}

const RAW_MONTHS: MonthRow[] = [
  { ym: '2025-09', subs: 6400, remote: 3200, field: 4900, exp: [2600, 1290, 420, 980, 600, 540, 1500], bankIn: 16900, bankOut: 19830, card: 7550, tax: 4920, contractor: 1500, ownerDraw: 5000, endBal: 21566, subsCount: 5 },
  { ym: '2025-10', subs: 6400, remote: 2800, field: 4300, exp: [2100, 1290, 420, 850, 600, 540, 1200], bankIn: 16350, bankOut: 15840, card: 6730, tax: 2050, contractor: 1200, ownerDraw: 5000, endBal: 22076, subsCount: 5 },
  { ym: '2025-11', subs: 6900, remote: 3600, field: 5700, exp: [3100, 1290, 420, 1050, 800, 540, 1800], bankIn: 17720, bankOut: 18679, card: 5980, tax: 5039, contractor: 1800, ownerDraw: 5000, endBal: 21117, subsCount: 6 },
  { ym: '2025-12', subs: 6900, remote: 3900, field: 6100, exp: [3400, 1330, 420, 1100, 800, 540, 2000], bankIn: 19660, bankOut: 17652, card: 7640, tax: 2152, contractor: 2000, ownerDraw: 5000, endBal: 23125, subsCount: 6 },
  { ym: '2026-01', subs: 7400, remote: 3400, field: 5600, exp: [2900, 1330, 420, 1000, 700, 540, 1800], bankIn: 19300, bankOut: 21505, card: 8100, tax: 5745, contractor: 1800, ownerDraw: 5000, endBal: 20920, subsCount: 7 },
  { ym: '2026-02', subs: 7400, remote: 3700, field: 5300, exp: [2700, 1330, 420, 950, 700, 540, 1600], bankIn: 19560, bankOut: 16894, card: 7270, tax: 2164, contractor: 1600, ownerDraw: 5000, endBal: 23586, subsCount: 7 },
  { ym: '2026-03', subs: 7900, remote: 4100, field: 6800, exp: [3800, 1360, 420, 1200, 900, 540, 2200], bankIn: 20720, bankOut: 20895, card: 6970, tax: 5865, contractor: 2200, ownerDraw: 5000, endBal: 23411, subsCount: 8 },
  { ym: '2026-04', subs: 7900, remote: 3300, field: 4900, exp: [2400, 1360, 420, 900, 700, 540, 1500], bankIn: 20340, bankOut: 18508, card: 8840, tax: 2308, contractor: 1500, ownerDraw: 5000, endBal: 25243, subsCount: 8 },
  { ym: '2026-05', subs: 8400, remote: 4400, field: 7200, exp: [3900, 1360, 420, 1250, 1000, 540, 2400], bankIn: 21570, bankOut: 20934, card: 6600, tax: 6074, contractor: 2400, ownerDraw: 5000, endBal: 25879, subsCount: 9 },
  { ym: '2026-06', subs: 8900, remote: 4600, field: 7600, exp: [4200, 1360, 420, 1300, 1000, 540, 2600], bankIn: 24210, bankOut: 19970, card: 9130, tax: 2380, contractor: 2600, ownerDraw: 5000, endBal: 30119, subsCount: 9 },
  { ym: '2026-07', subs: 9300, remote: 4200, field: 8100, exp: [4500, 1390, 420, 1400, 1100, 540, 2800], bankIn: 24810, bankOut: 25271, card: 9550, tax: 7061, contractor: 2800, ownerDraw: 5000, endBal: 29658, subsCount: 9 },
  { ym: '2026-08', subs: 9800, remote: 3900, field: 7400, exp: [4100, 1390, 420, 1350, 1000, 540, 2600], bankIn: 24990, bankOut: 21106, card: 10170, tax: 2476, contractor: 2600, ownerDraw: 5000, endBal: 33542, subsCount: 8 },
  { ym: '2026-09', subs: 9800, remote: 600, field: 1500, exp: [1194, 480, 318, 300, 0, 90, 0], bankIn: 10207, bankOut: 5329, card: 0, tax: 0, contractor: 0, ownerDraw: 5000, endBal: 38420, subsCount: 8, partial: true },
];

const CUR = RAW_MONTHS.length - 1;
const CATS: ExpenseCategory[] = ['EQUIPMENT', 'SOFTWARE', 'TELECOM', 'VEHICLE', 'MARKETING', 'BOOKKEEPING', 'CONTRACTORS'];

/** Same-days comparison for the running month (spec §7.2: August 1–6). */
const AUG_1_6 = { income: 11200, expense: 2080, profit: 9120, invoices: 9, expenses: 7 };

function monthIndex(ym: MonthString): number {
  return RAW_MONTHS.findIndex((m) => m.ym === ym);
}
function incomeOf(m: MonthRow): number {
  return m.subs + m.remote + m.field;
}
function expenseOf(m: MonthRow): number {
  return m.exp.reduce((a, b) => a + b, 0);
}

// ───────────────────────────── customers, subscriptions, calls ─────────────────────────────

interface RawCustomer {
  id: string;
  name: string;
  type: 'business' | 'private';
  method: PaymentMethod;
  avgDays: number | null;
  rev12: number;
  email: string | null;
}

const RAW_CUSTOMERS: RawCustomer[] = [
  { id: 'c01', name: 'משרד עו"ד לוי ושות\'', type: 'business', method: 'transfer', avgDays: 33, rev12: 24600, email: 'office@levi-law.co.il' },
  { id: 'c02', name: 'חברת נדל"ן אבני', type: 'business', method: 'transfer', avgDays: 52, rev12: 20100, email: 'hanhala@avni-re.co.il' },
  { id: 'c03', name: 'מרפאת שיניים ד"ר כהן', type: 'business', method: 'standing_order', avgDays: 0, rev12: 18400, email: 'clinic@drcohen.co.il' },
  { id: 'c04', name: 'משרד רו"ח ברק', type: 'business', method: 'transfer', avgDays: 29, rev12: 15900, email: 'barak@barak-cpa.co.il' },
  { id: 'c05', name: 'סטודיו פיקסל', type: 'business', method: 'transfer', avgDays: 41, rev12: 15200, email: 'hello@pixel.studio' },
  { id: 'c06', name: 'מכון כושר FitZone', type: 'business', method: 'standing_order', avgDays: 18, rev12: 11700, email: 'info@fitzone.co.il' },
  { id: 'c07', name: 'קליניקת אור', type: 'business', method: 'standing_order', avgDays: 0, rev12: 10300, email: 'or@clinic-or.co.il' },
  { id: 'c08', name: 'מסעדת הגפן', type: 'business', method: 'transfer', avgDays: 61, rev12: 7900, email: 'hagefen@gmail.com' },
  { id: 'c09', name: 'טכנו-סטור', type: 'business', method: 'transfer', avgDays: 35, rev12: 5400, email: 'techno@store.co.il' },
  { id: 'p01', name: 'יוסי אברהמי', type: 'private', method: 'card', avgDays: 0, rev12: 0, email: null },
  { id: 'p02', name: 'רונית שפירא', type: 'private', method: 'card', avgDays: 0, rev12: 0, email: null },
  { id: 'p03', name: 'דנה מזרחי', type: 'private', method: 'card', avgDays: 0, rev12: 0, email: null },
  { id: 'p04', name: 'אלי ביטון', type: 'private', method: 'card', avgDays: 0, rev12: 0, email: null },
  { id: 'p05', name: 'מיכל פרידמן', type: 'private', method: 'card', avgDays: 0, rev12: 0, email: null },
  { id: 'p06', name: 'נועה גל', type: 'private', method: 'card', avgDays: 0, rev12: 0, email: null },
];

const CUSTOMER_BY_ID = new Map(RAW_CUSTOMERS.map((c) => [c.id, c]));
function customerName(id: string): string {
  return CUSTOMER_BY_ID.get(id)?.name ?? id;
}

const SUBSCRIPTIONS: Subscription[] = [
  { id: 's01', customerId: 'c01', customerName: customerName('c01'), plan: 'פרימיום', seats: 12, monthlyNetAgorot: ILS(1900), billingMethod: 'invoice_net30', nextChargeDate: '2026-10-01', status: 'active', startedAt: '2025-03-01', cancelledAt: null },
  { id: 's02', customerId: 'c02', customerName: customerName('c02'), plan: 'עסקי', seats: 10, monthlyNetAgorot: ILS(1600), billingMethod: 'invoice_net30', nextChargeDate: '2026-10-01', status: 'active', startedAt: '2025-06-01', cancelledAt: null },
  { id: 's03', customerId: 'c03', customerName: customerName('c03'), plan: 'עסקי', seats: 8, monthlyNetAgorot: ILS(1400), billingMethod: 'standing_order', nextChargeDate: '2026-10-01', status: 'active', startedAt: '2025-01-01', cancelledAt: null },
  { id: 's04', customerId: 'c04', customerName: customerName('c04'), plan: 'עסקי', seats: 7, monthlyNetAgorot: ILS(1300), billingMethod: 'invoice_net30', nextChargeDate: '2026-10-01', status: 'active', startedAt: '2025-11-01', cancelledAt: null },
  { id: 's05', customerId: 'c05', customerName: customerName('c05'), plan: 'עסקי', seats: 6, monthlyNetAgorot: ILS(1200), billingMethod: 'invoice_net30', nextChargeDate: '2026-10-01', status: 'active', startedAt: '2026-01-01', cancelledAt: null },
  { id: 's06', customerId: 'c06', customerName: customerName('c06'), plan: 'בסיס', seats: 4, monthlyNetAgorot: ILS(900), billingMethod: 'standing_order', nextChargeDate: '2026-10-01', status: 'active', startedAt: '2026-03-01', cancelledAt: null },
  { id: 's07', customerId: 'c07', customerName: customerName('c07'), plan: 'בסיס', seats: 3, monthlyNetAgorot: ILS(800), billingMethod: 'standing_order', nextChargeDate: '2026-10-01', status: 'active', startedAt: '2026-05-01', cancelledAt: null },
  { id: 's08', customerId: 'c08', customerName: customerName('c08'), plan: 'בסיס', seats: 2, monthlyNetAgorot: ILS(700), billingMethod: 'invoice_net30', nextChargeDate: '2026-10-01', status: 'active', startedAt: '2025-09-01', cancelledAt: null },
  { id: 's09', customerId: 'c09', customerName: customerName('c09'), plan: 'בסיס', seats: 3, monthlyNetAgorot: ILS(600), billingMethod: 'invoice_net30', nextChargeDate: null, status: 'cancelled', startedAt: '2025-10-01', cancelledAt: '2026-07-31' },
];

const CALLS: ServiceCall[] = [
  { id: 'LT-2026-0921', customerId: 'c01', customerName: customerName('c01'), type: 'remote', status: 'open', openedAt: '2026-09-06T08:40:00+03:00', closedAt: null, technician: 'יאיר', priceNetAgorot: null, estimate: false, source: 'sentinel', finding: 'דיסק 92%', durationHours: null },
  { id: 'LT-2026-0920', customerId: 'c06', customerName: customerName('c06'), type: 'field', status: 'in_progress', openedAt: '2026-09-05T10:15:00+03:00', closedAt: null, technician: 'א. סבן', priceNetAgorot: ILS(1250), estimate: false, source: 'customer', finding: null, durationHours: null },
  { id: 'LT-2026-0919', customerId: 'p03', customerName: customerName('p03'), type: 'remote', status: 'done', openedAt: '2026-09-05T09:00:00+03:00', closedAt: '2026-09-05T11:20:00+03:00', technician: 'יאיר', priceNetAgorot: ILS(350), estimate: false, source: 'customer', finding: null, durationHours: 2.3 },
  { id: 'LT-2026-0918', customerId: 'c02', customerName: customerName('c02'), type: 'remote', status: 'in_progress', openedAt: '2026-09-04T14:10:00+03:00', closedAt: null, technician: 'יאיר', priceNetAgorot: null, estimate: false, source: 'sentinel', finding: 'גיבוי נכשל', durationHours: null },
  { id: 'LT-2026-0917', customerId: 'p01', customerName: customerName('p01'), type: 'remote', status: 'done', openedAt: '2026-09-04T09:30:00+03:00', closedAt: '2026-09-04T10:15:00+03:00', technician: 'יאיר', priceNetAgorot: ILS(250), estimate: false, source: 'customer', finding: null, durationHours: 0.75 },
  { id: 'LT-2026-0916', customerId: 'c07', customerName: customerName('c07'), type: 'field', status: 'done', openedAt: '2026-09-03T08:00:00+03:00', closedAt: '2026-09-03T13:30:00+03:00', technician: 'יאיר', priceNetAgorot: ILS(950), estimate: false, source: 'customer', finding: null, durationHours: 5.5 },
  { id: 'LT-2026-0915', customerId: 'p02', customerName: customerName('p02'), type: 'field', status: 'done', openedAt: '2026-09-03T09:00:00+03:00', closedAt: '2026-09-03T12:00:00+03:00', technician: 'א. סבן', priceNetAgorot: ILS(550), estimate: false, source: 'customer', finding: null, durationHours: 3 },
  { id: 'LT-2026-0914', customerId: 'c03', customerName: customerName('c03'), type: 'remote', status: 'in_progress', openedAt: '2026-09-02T07:50:00+03:00', closedAt: null, technician: 'יאיר', priceNetAgorot: null, estimate: false, source: 'sentinel', finding: 'עדכוני Windows', durationHours: null },
  { id: 'LT-2026-0913', customerId: 'c05', customerName: customerName('c05'), type: 'field', status: 'open', openedAt: '2026-09-02T11:00:00+03:00', closedAt: null, technician: null, priceNetAgorot: ILS(800), estimate: true, source: 'customer', finding: null, durationHours: null },
  { id: 'LT-2026-0912', customerId: 'p04', customerName: customerName('p04'), type: 'remote', status: 'open', openedAt: '2026-09-01T16:20:00+03:00', closedAt: null, technician: null, priceNetAgorot: ILS(250), estimate: false, source: 'customer', finding: null, durationHours: null },
];

// ───────────────────────────── invoices (spec §7.6) ─────────────────────────────

interface RawInvoice {
  number: string;
  issueDate: DayString;
  dueDate: DayString;
  customerId: string;
  label: string | null;
  net: number;
  vat: number;
  gross: number;
  method: PaymentMethod;
  status: InvoiceStatus;
  docType: DocType;
  paidAt?: DayString;
  matchedTxId?: string;
  reminders?: DayString[];
}

const RAW_INVOICES: RawInvoice[] = [
  { number: '2026-0317', issueDate: '2026-05-05', dueDate: '2026-06-04', customerId: 'c08', label: null, net: 700, vat: 126, gross: 826, method: 'transfer', status: 'overdue', docType: 'proforma', reminders: ['2026-06-20', '2026-07-15', '2026-08-10'] },
  { number: '2026-0336', issueDate: '2026-06-02', dueDate: '2026-07-02', customerId: 'c05', label: null, net: 1200, vat: 216, gross: 1416, method: 'transfer', status: 'overdue', docType: 'proforma', reminders: ['2026-07-20', '2026-08-10'] },
  { number: '2026-0344', issueDate: '2026-06-12', dueDate: '2026-07-12', customerId: 'c04', label: 'ביקור שטח', net: 850, vat: 153, gross: 1003, method: 'transfer', status: 'overdue', docType: 'proforma', reminders: ['2026-08-10'] },
  { number: '2026-0359', issueDate: '2026-07-01', dueDate: '2026-07-31', customerId: 'c02', label: null, net: 1600, vat: 288, gross: 1888, method: 'transfer', status: 'overdue', docType: 'proforma', reminders: ['2026-08-10'] },
  { number: '2026-0368', issueDate: '2026-07-14', dueDate: '2026-08-13', customerId: 'c06', label: 'ביקור שטח', net: 1250, vat: 225, gross: 1475, method: 'transfer', status: 'overdue', docType: 'proforma' },
  { number: '2026-0381', issueDate: '2026-08-01', dueDate: '2026-08-31', customerId: 'c02', label: null, net: 1600, vat: 288, gross: 1888, method: 'transfer', status: 'overdue', docType: 'proforma' },
  { number: '2026-0379', issueDate: '2026-08-01', dueDate: '2026-08-31', customerId: 'c01', label: null, net: 1900, vat: 342, gross: 2242, method: 'transfer', status: 'paid', docType: 'tax_receipt', paidAt: '2026-09-04', matchedTxId: 't05' },
  { number: '2026-0380', issueDate: '2026-08-01', dueDate: '2026-08-31', customerId: 'c05', label: null, net: 1200, vat: 216, gross: 1416, method: 'transfer', status: 'paid', docType: 'tax_receipt', paidAt: '2026-09-01', matchedTxId: 't14' },
  { number: '2026-0382', issueDate: '2026-08-01', dueDate: '2026-08-31', customerId: 'c04', label: null, net: 1300, vat: 234, gross: 1534, method: 'transfer', status: 'paid', docType: 'tax_receipt', paidAt: '2026-09-03', matchedTxId: 't07' },
  { number: '2026-0401', issueDate: '2026-09-01', dueDate: '2026-10-01', customerId: 'c01', label: null, net: 1900, vat: 342, gross: 2242, method: 'transfer', status: 'open', docType: 'proforma' },
  { number: '2026-0402', issueDate: '2026-09-01', dueDate: '2026-10-01', customerId: 'c05', label: null, net: 1200, vat: 216, gross: 1416, method: 'transfer', status: 'open', docType: 'proforma' },
  { number: '2026-0403', issueDate: '2026-09-01', dueDate: '2026-10-01', customerId: 'c02', label: null, net: 1600, vat: 288, gross: 1888, method: 'transfer', status: 'open', docType: 'proforma' },
  { number: '2026-0404', issueDate: '2026-09-01', dueDate: '2026-10-01', customerId: 'c04', label: null, net: 1300, vat: 234, gross: 1534, method: 'transfer', status: 'open', docType: 'proforma' },
  { number: '2026-0405', issueDate: '2026-09-01', dueDate: '2026-10-01', customerId: 'c08', label: null, net: 700, vat: 126, gross: 826, method: 'transfer', status: 'open', docType: 'proforma' },
  { number: '2026-0406', issueDate: '2026-09-01', dueDate: '2026-09-01', customerId: 'c03', label: null, net: 1400, vat: 252, gross: 1652, method: 'standing_order', status: 'paid', docType: 'tax_receipt', paidAt: '2026-09-01', matchedTxId: 't01' },
  { number: '2026-0407', issueDate: '2026-09-01', dueDate: '2026-09-01', customerId: 'c06', label: null, net: 900, vat: 162, gross: 1062, method: 'standing_order', status: 'paid', docType: 'tax_receipt', paidAt: '2026-09-01', matchedTxId: 't01' },
  { number: '2026-0408', issueDate: '2026-09-01', dueDate: '2026-09-01', customerId: 'c07', label: null, net: 800, vat: 144, gross: 944, method: 'standing_order', status: 'paid', docType: 'tax_receipt', paidAt: '2026-09-01', matchedTxId: 't01' },
  { number: '2026-0409', issueDate: '2026-09-03', dueDate: '2026-10-03', customerId: 'c07', label: 'ביקור שטח', net: 950, vat: 171, gross: 1121, method: 'transfer', status: 'open', docType: 'proforma' },
  { number: '2026-0410', issueDate: '2026-09-03', dueDate: '2026-09-03', customerId: 'p02', label: 'ביקור שטח', net: 550, vat: 99, gross: 649, method: 'card', status: 'paid', docType: 'tax_receipt', paidAt: '2026-09-03', matchedTxId: 't02' },
  { number: '2026-0411', issueDate: '2026-09-04', dueDate: '2026-09-04', customerId: 'p01', label: 'תמיכה מרחוק', net: 250, vat: 45, gross: 295, method: 'card', status: 'paid', docType: 'tax_receipt', paidAt: '2026-09-04', matchedTxId: 't02' },
  { number: '2026-0412', issueDate: '2026-09-05', dueDate: '2026-09-05', customerId: 'p03', label: 'תמיכה מרחוק', net: 350, vat: 63, gross: 413, method: 'card', status: 'paid', docType: 'tax_receipt', paidAt: '2026-09-05', matchedTxId: 't02' },
];

function serviceLineOf(label: string | null): ServiceLine {
  if (label === 'ביקור שטח') return 'field';
  if (label === 'תמיכה מרחוק') return 'remote';
  return 'subscriptions';
}

function bucketOf(days: number): AgingBucket {
  if (days <= 30) return '0-30';
  if (days <= 60) return '31-60';
  if (days <= 90) return '61-90';
  return '90+';
}

function vatPeriodIdOf(day: DayString): string {
  const [y, m] = parts(day);
  const start = m % 2 === 1 ? m : m - 1;
  return `${y}-${String(start).padStart(2, '0')}`;
}

function toInvoice(raw: RawInvoice): Invoice {
  const overdueDays = raw.status === 'overdue' ? daysBetween(raw.dueDate, TODAY) : null;
  return {
    number: raw.number,
    docType: raw.docType,
    customerId: raw.customerId,
    customerName: customerName(raw.customerId),
    grossAgorot: ILS(raw.gross),
    status: raw.status,
    issueDate: raw.issueDate,
    dueDate: raw.dueDate,
    netAgorot: ILS(raw.net),
    vatAgorot: ILS(raw.vat),
    method: raw.method,
    label: raw.label,
    serviceLine: serviceLineOf(raw.label),
    paidAt: raw.paidAt ?? null,
    overdueDays,
    agingBucket: overdueDays == null ? null : bucketOf(overdueDays),
    matchedTxId: raw.matchedTxId ?? null,
    matchKind: raw.matchedTxId ? (raw.method === 'transfer' ? 'direct' : 'hyp_batch') : null,
    statusSource: raw.method === 'transfer' ? 'local' : 'hyp',
    source: 'hyp',
    reminders: raw.reminders ?? [],
    note: null,
    vatPeriod: raw.paidAt ? vatPeriodIdOf(raw.paidAt) : null,
    updatedAt: LAST_FETCH,
  };
}

const INVOICES: Invoice[] = RAW_INVOICES.map(toInvoice);

// ───────────────────────────── transactions (spec §7.9) ─────────────────────────────

interface RawTx {
  id: string;
  date: DayString;
  desc: string;
  sub?: string;
  account: 'checking' | 'card';
  cat: ExpenseCategory | 'RECEIPT' | 'TAX' | 'CARD_SETTLEMENT' | 'OWNER_DRAW';
  amount: number;
  net?: number;
  vat?: number;
  inv?: true | false | 'exempt' | 'na';
  foreign?: boolean;
  markupFee?: number;
  origMinor?: number;
  origCurrency?: string;
  status?: 'PENDING';
  match?: { kind: 'direct' | 'hyp_batch'; inv: string[]; batchId?: string };
  supplier?: string;
}

const RAW_TX: RawTx[] = [
  { id: 't01', date: '2026-09-06', desc: 'HYP סליקה — הוראות קבע', sub: 'חיוב 1.9, סליקה +5', account: 'checking', cat: 'RECEIPT', amount: 3658, match: { kind: 'hyp_batch', inv: ['2026-0406', '2026-0407', '2026-0408'], batchId: 'HYP-2026-09-06-SO' } },
  { id: 't02', date: '2026-09-06', desc: 'HYP סליקה — כרטיסים', sub: 'עסקאות 3–5.9', account: 'checking', cat: 'RECEIPT', amount: 1357, match: { kind: 'hyp_batch', inv: ['2026-0410', '2026-0411', '2026-0412'], batchId: 'HYP-2026-09-06-CC' } },
  { id: 't03', date: '2026-09-06', desc: 'סלקום', account: 'card', cat: 'TELECOM', amount: -152, net: 129, vat: 23, inv: true, status: 'PENDING', supplier: 'sup-cellcom' },
  { id: 't04', date: '2026-09-05', desc: 'Google Workspace', account: 'card', cat: 'SOFTWARE', amount: -319, net: 270, vat: 49, inv: true, supplier: 'sup-google' },
  { id: 't05', date: '2026-09-04', desc: 'העברה — משרד עו"ד לוי ושות\'', account: 'checking', cat: 'RECEIPT', amount: 2242, match: { kind: 'direct', inv: ['2026-0379'] } },
  { id: 't06', date: '2026-09-04', desc: 'AliExpress — מתאמי USB-C', sub: 'מטבע מקורי $25', account: 'card', cat: 'EQUIPMENT', amount: -94, net: 94, vat: 0, inv: 'na', foreign: true, origMinor: 2500, origCurrency: 'USD' },
  { id: 't07', date: '2026-09-03', desc: 'העברה — משרד רו"ח ברק', account: 'checking', cat: 'RECEIPT', amount: 1534, match: { kind: 'direct', inv: ['2026-0382'] } },
  { id: 't08', date: '2026-09-03', desc: 'HYP — עמלות סליקה אוגוסט', account: 'checking', cat: 'BOOKKEEPING', amount: -106, net: 90, vat: 16, inv: true, supplier: 'sup-hyp' },
  { id: 't09', date: '2026-09-02', desc: 'KSP מחשבים', account: 'card', cat: 'EQUIPMENT', amount: -1298, net: 1100, vat: 198, inv: true },
  { id: 't10', date: '2026-09-02', desc: 'פז', account: 'card', cat: 'VEHICLE', amount: -354, net: 300, vat: 54, inv: true },
  { id: 't11', date: '2026-09-02', desc: 'העברה לחשבון פרטי — משיכת בעלים', account: 'checking', cat: 'OWNER_DRAW', amount: -5000 },
  { id: 't12', date: '2026-09-01', desc: 'Hetzner', account: 'card', cat: 'SOFTWARE', amount: -248, net: 210, vat: 38, inv: true, supplier: 'sup-hetzner' },
  { id: 't13', date: '2026-09-01', desc: 'בזק — הוראת קבע', account: 'checking', cat: 'TELECOM', amount: -223, net: 189, vat: 34, inv: true, supplier: 'sup-bezeq' },
  { id: 't14', date: '2026-09-01', desc: 'העברה — סטודיו פיקסל', account: 'checking', cat: 'RECEIPT', amount: 1416, match: { kind: 'direct', inv: ['2026-0380'] } },
  { id: 't15', date: '2026-08-31', desc: 'Google Ads', account: 'card', cat: 'MARKETING', amount: -708, net: 600, vat: 108, inv: true },
  { id: 't16', date: '2026-08-28', desc: 'העברה נכנסת — ב. ברק', account: 'checking', cat: 'RECEIPT', amount: 1003 },
  { id: 't17', date: '2026-08-27', desc: 'Bug — כבלים ומתאמים', account: 'card', cat: 'EQUIPMENT', amount: -412, net: 349, vat: 63, inv: false },
  { id: 't18', date: '2026-08-25', desc: 'העברה — א. סבן (טכנאי קבלן)', account: 'checking', cat: 'CONTRACTORS', amount: -2600, net: 2600, vat: 0, inv: 'exempt' },
  { id: 't19', date: '2026-08-25', desc: 'העברה — רו"ח שמעוני', account: 'checking', cat: 'BOOKKEEPING', amount: -531, net: 450, vat: 81, inv: true, supplier: 'sup-cpa' },
  { id: 't20', date: '2026-08-24', desc: 'פז', account: 'card', cat: 'VEHICLE', amount: -587, net: 497, vat: 90, inv: true },
  { id: 't21', date: '2026-08-22', desc: 'Canva', account: 'card', cat: 'SOFTWARE', amount: -71, net: 60, vat: 11, inv: true, supplier: 'sup-canva' },
  { id: 't22', date: '2026-08-21', desc: 'KSP מחשבים', account: 'card', cat: 'EQUIPMENT', amount: -661, net: 560, vat: 101, inv: true },
  { id: 't23', date: '2026-08-20', desc: 'AnyDesk', account: 'card', cat: 'SOFTWARE', amount: -448, net: 380, vat: 68, inv: true, supplier: 'sup-anydesk' },
  { id: 't24', date: '2026-08-20', desc: 'Facebook Ads', account: 'card', cat: 'MARKETING', amount: -472, net: 400, vat: 72, inv: true },
  { id: 't25', date: '2026-08-19', desc: 'Ivory', account: 'card', cat: 'EQUIPMENT', amount: -1062, net: 900, vat: 162, inv: true },
  { id: 't26', date: '2026-08-18', desc: 'Zoho Desk', account: 'card', cat: 'SOFTWARE', amount: -153, net: 130, vat: 23, inv: true, supplier: 'sup-zoho' },
  { id: 't27', date: '2026-08-15', desc: 'רשות המסים — מקדמת מ"ה', account: 'checking', cat: 'TAX', amount: -1296 },
  { id: 't28', date: '2026-08-15', desc: 'ביטוח לאומי', account: 'checking', cat: 'TAX', amount: -1180 },
  { id: 't29', date: '2026-08-15', desc: 'MeshCentral hosting', account: 'card', cat: 'SOFTWARE', amount: -212, net: 180, vat: 32, inv: true, supplier: 'sup-mesh' },
  { id: 't30', date: '2026-08-15', desc: 'Microsoft 365', account: 'card', cat: 'SOFTWARE', amount: -142, net: 120, vat: 22, inv: true, supplier: 'sup-ms' },
  { id: 't31', date: '2026-08-13', desc: 'סונול', account: 'card', cat: 'VEHICLE', amount: -486, net: 412, vat: 74, inv: true },
  { id: 't32', date: '2026-08-12', desc: 'Ivory', account: 'card', cat: 'EQUIPMENT', amount: -343, net: 291, vat: 52, inv: false },
  { id: 't33', date: '2026-08-12', desc: 'פרטנר סיבים', account: 'card', cat: 'TELECOM', amount: -120, net: 102, vat: 18, inv: true, supplier: 'sup-partner' },
  { id: 't34', date: '2026-08-10', desc: 'חיוב כרטיס Cal ****7712', sub: 'מחזור יולי', account: 'checking', cat: 'CARD_SETTLEMENT', amount: -10170 },
  { id: 't35', date: '2026-08-10', desc: 'HYP סליקה — כרטיסים', sub: 'עסקאות 5–9.8', account: 'checking', cat: 'RECEIPT', amount: 2714, match: { kind: 'hyp_batch', inv: ['2026-0388', '2026-0389', '2026-0390', '2026-0391'], batchId: 'HYP-2026-08-10-CC' } },
  { id: 't36', date: '2026-08-09', desc: 'העברה — סטודיו פיקסל (ביקור)', account: 'checking', cat: 'RECEIPT', amount: 1272, match: { kind: 'direct', inv: ['2026-0371'] } },
  { id: 't37', date: '2026-08-08', desc: 'העברה — חברת נדל"ן אבני (ביקור שטח)', account: 'checking', cat: 'RECEIPT', amount: 1475, match: { kind: 'direct', inv: ['2026-0366'] } },
  { id: 't38', date: '2026-08-08', desc: 'Cloudflare', sub: 'מטבע מקורי $11 · כולל עמלת המרה ₪4', account: 'card', cat: 'SOFTWARE', amount: -44, net: 40, vat: 0, inv: 'na', foreign: true, markupFee: 4, origMinor: 1100, origCurrency: 'USD', supplier: 'sup-cloudflare' },
  { id: 't39', date: '2026-08-07', desc: 'HYP סליקה — כרטיסים', sub: 'עסקאות 1–4.8', account: 'checking', cat: 'RECEIPT', amount: 1947, match: { kind: 'hyp_batch', inv: ['2026-0386', '2026-0387'], batchId: 'HYP-2026-08-07-CC' } },
  { id: 't40', date: '2026-08-07', desc: 'העברה — משרד עו"ד לוי (ביקור שטח)', account: 'checking', cat: 'RECEIPT', amount: 2360, match: { kind: 'direct', inv: ['2026-0372'] } },
  { id: 't41', date: '2026-08-06', desc: 'HYP סליקה — הוראות קבע', sub: 'חיוב 1.8, סליקה +5', account: 'checking', cat: 'RECEIPT', amount: 3658, match: { kind: 'hyp_batch', inv: ['2026-0383', '2026-0384', '2026-0385'], batchId: 'HYP-2026-08-06-SO' } },
  { id: 't42', date: '2026-08-06', desc: 'סלקום', account: 'card', cat: 'TELECOM', amount: -152, net: 129, vat: 23, inv: true, supplier: 'sup-cellcom' },
  { id: 't43', date: '2026-08-05', desc: 'Google Workspace', account: 'card', cat: 'SOFTWARE', amount: -319, net: 270, vat: 49, inv: true, supplier: 'sup-google' },
  { id: 't44', date: '2026-08-05', desc: 'העברה — קליניקת אור (ביקור)', account: 'checking', cat: 'RECEIPT', amount: 1121, match: { kind: 'direct', inv: ['2026-0369'] } },
  { id: 't45', date: '2026-08-04', desc: 'העברה — משרד עו"ד לוי ושות\'', account: 'checking', cat: 'RECEIPT', amount: 2242, match: { kind: 'direct', inv: ['2026-0357'] } },
  { id: 't46', date: '2026-08-04', desc: 'העברה — משרד רו"ח ברק', account: 'checking', cat: 'RECEIPT', amount: 1534, match: { kind: 'direct', inv: ['2026-0360'] } },
  { id: 't47', date: '2026-08-04', desc: 'פז', account: 'card', cat: 'VEHICLE', amount: -520, net: 441, vat: 79, inv: true },
  { id: 't48', date: '2026-08-03', desc: 'העברה — סטודיו פיקסל', account: 'checking', cat: 'RECEIPT', amount: 1416, match: { kind: 'direct', inv: ['2026-0358'] } },
  { id: 't49', date: '2026-08-03', desc: 'העברה — מרפאת ד"ר כהן (ביקור שטח)', account: 'checking', cat: 'RECEIPT', amount: 1652, match: { kind: 'direct', inv: ['2026-0367'] } },
  { id: 't50', date: '2026-08-03', desc: 'HYP — עמלות סליקה יולי', account: 'checking', cat: 'BOOKKEEPING', amount: -106, net: 90, vat: 16, inv: true, supplier: 'sup-hyp' },
  { id: 't51', date: '2026-08-03', desc: 'KSP מחשבים', account: 'card', cat: 'EQUIPMENT', amount: -2360, net: 2000, vat: 360, inv: true },
  { id: 't52', date: '2026-08-02', desc: 'העברה לחשבון פרטי — משיכת בעלים', account: 'checking', cat: 'OWNER_DRAW', amount: -5000 },
  { id: 't53', date: '2026-08-01', desc: 'HYP סליקה — כרטיסים', sub: 'עסקאות 27–31.7', account: 'checking', cat: 'RECEIPT', amount: 2596, match: { kind: 'hyp_batch', inv: ['2026-0375', '2026-0376', '2026-0377', '2026-0378'], batchId: 'HYP-2026-08-01-CC' } },
  { id: 't54', date: '2026-08-01', desc: 'בזק — הוראת קבע', account: 'checking', cat: 'TELECOM', amount: -223, net: 189, vat: 34, inv: true, supplier: 'sup-bezeq' },
  { id: 't55', date: '2026-08-01', desc: 'Hetzner', account: 'card', cat: 'SOFTWARE', amount: -248, net: 210, vat: 38, inv: true, supplier: 'sup-hetzner' },
];

/** Local overrides the demo adapter keeps in memory (category edits, "יש חשבונית", matches). */
const txOverrides = new Map<string, { category?: ExpenseCategory; hasTaxInvoice?: boolean; note?: string | null; match?: TransactionRow['match'] }>();

function vatShareOf(raw: RawTx, hasInvoice: boolean): number {
  if (!hasInvoice) return 0;
  if (raw.inv === 'exempt' || raw.inv === 'na') return 0;
  const cat = txCategory(raw);
  if (cat === 'VEHICLE') return 2 / 3;
  return 1;
}

function txCategory(raw: RawTx): ExpenseCategory | null {
  const override = txOverrides.get(raw.id)?.category;
  if (override) return override;
  return (CATS as string[]).includes(raw.cat) ? (raw.cat as ExpenseCategory) : null;
}

function expenseRefOf(raw: RawTx): ExpenseRef | null {
  const category = txCategory(raw);
  if (!category || raw.amount >= 0) return null;
  const override = txOverrides.get(raw.id);
  const hasTaxInvoice = override?.hasTaxInvoice ?? (raw.inv === true || raw.inv === 'exempt');
  const share = vatShareOf(raw, hasTaxInvoice);
  const vat = ILS(raw.vat ?? 0);
  const supplier = raw.supplier ? SUPPLIERS.find((s) => s.id === raw.supplier) : undefined;
  return {
    category,
    categorySource: override?.category ? 'manual' : 'auto',
    grossAgorot: ILS(Math.abs(raw.amount)),
    netAgorot: ILS(raw.net ?? Math.abs(raw.amount)),
    vatAgorot: vat,
    hasTaxInvoice,
    vatShare: share,
    deductibleVatAgorot: Math.round(vat * share),
    foreign: Boolean(raw.foreign),
    paymentChannel: raw.account === 'card' ? 'card' : supplier?.paymentChannel ?? 'bank_transfer',
    supplierId: supplier?.id ?? null,
    supplierName: supplier?.name ?? null,
  };
}

function scopeOf(raw: RawTx): TransactionRow['scope'] {
  switch (raw.cat) {
    case 'RECEIPT':
      return 'receipt';
    case 'TAX':
      return 'tax';
    case 'CARD_SETTLEMENT':
      return 'card_settlement';
    case 'OWNER_DRAW':
      return 'owner_draw';
    default:
      return 'expense';
  }
}

function toTx(raw: RawTx): TransactionRow {
  const override = txOverrides.get(raw.id);
  const match = override && 'match' in override ? override.match ?? null : raw.match
    ? { kind: raw.match.kind, invoiceNumbers: raw.match.inv, matchedAt: LAST_FETCH, matchedBy: 'sync', batchId: raw.match.batchId ?? null }
    : null;
  const isCard = raw.account === 'card';
  return {
    id: raw.id,
    account: isCard ? CARD_REF : CHECKING_REF,
    kind: isCard ? 'CARD' : 'CHECKING',
    status: raw.status === 'PENDING' ? 'PENDING' : 'BOOKED',
    effDate: raw.date,
    txDate: isCard ? raw.date : null,
    bookingDate: raw.status === 'PENDING' ? null : raw.date,
    valueDate: raw.date,
    amountAgorot: ILS(raw.amount),
    amountMinor: ILS(raw.amount),
    currency: 'ILS',
    origAmountMinor: raw.origMinor ?? null,
    origCurrency: raw.origCurrency ?? null,
    markupFeeAgorot: raw.markupFee ? ILS(raw.markupFee) : null,
    description: raw.desc + (raw.sub ? ` · ${raw.sub}` : ''),
    merchant: raw.desc,
    merchantKey: raw.desc.toLowerCase(),
    category: { main: null, sub: null },
    scope: scopeOf(raw),
    expense: expenseRefOf(raw),
    match,
    installment: null,
    balanceEndDayAgorot: null,
    classificationType: null,
    cycleMonth: isCard ? monthOf(raw.date) : null,
    note: override?.note ?? null,
    superseded: false,
    supersededBy: null,
    manual: false,
  };
}

function allTx(): TransactionRow[] {
  return RAW_TX.map(toTx);
}

// ───────────────────────────── suppliers (spec §7.8) ─────────────────────────────

const SUPPLIERS: Supplier[] = [
  ['sup-google', 'Google Workspace', 'SOFTWARE', 270, 5, 'card', 'auto', false, null],
  ['sup-anydesk', 'AnyDesk', 'SOFTWARE', 380, 20, 'card', 'auto', false, null],
  ['sup-hetzner', 'Hetzner VPS', 'SOFTWARE', 210, 1, 'card', 'auto', false, null],
  ['sup-mesh', 'MeshCentral hosting', 'SOFTWARE', 180, 15, 'card', 'auto', false, null],
  ['sup-zoho', 'Zoho Desk', 'SOFTWARE', 130, 18, 'card', 'auto', false, null],
  ['sup-ms', 'Microsoft 365', 'SOFTWARE', 120, 15, 'card', 'auto', false, null],
  ['sup-canva', 'Canva', 'SOFTWARE', 60, 22, 'card', 'manual', false, null],
  ['sup-cloudflare', 'Cloudflare', 'SOFTWARE', 40, 8, 'card', 'auto', true, '$11'],
  ['sup-cellcom', 'סלקום', 'TELECOM', 129, 6, 'card', 'auto', false, null],
  ['sup-partner', 'פרטנר סיבים', 'TELECOM', 102, 12, 'card', 'auto', false, null],
  ['sup-bezeq', 'בזק', 'TELECOM', 189, 1, 'standing_order', 'auto', false, null],
  ['sup-cpa', 'רו"ח שמעוני', 'BOOKKEEPING', 450, 25, 'bank_transfer', 'manual', false, null],
  ['sup-hyp', 'HYP — עמלות סליקה', 'BOOKKEEPING', 90, 3, 'bank_transfer', 'auto', false, null],
].map(([id, name, category, net, day, channel, mode, foreign, orig]) => ({
  id: id as string,
  name: name as string,
  category: category as ExpenseCategory,
  recurring: true,
  expectedMonthlyNetAgorot: ILS(net as number),
  chargeDay: day as number,
  nextChargeDate: nextChargeFor(day as number),
  invoiceMode: mode as 'auto' | 'manual',
  paymentChannel: channel as PaymentChannel,
  foreign: foreign as boolean,
  merchantKeys: [(name as string).toLowerCase()],
  chargedThisMonthAgorot: (day as number) <= 6 ? ILS(net as number) : 0,
  updatedAt: LAST_FETCH,
  origLabel: orig as string | null,
})) as Array<Supplier & { origLabel: string | null }>;

function nextChargeFor(day: number): DayString {
  const [y, m] = parts(TODAY);
  const thisMonth = `${y}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return thisMonth > TODAY ? thisMonth : `${monthStart(addMonths(monthOf(TODAY), 1)).slice(0, 8)}${String(day).padStart(2, '0')}`;
}

// ───────────────────────────── VAT (spec §7.7) ─────────────────────────────

interface RawVat {
  id: string;
  label: string;
  from: DayString;
  to: DayString;
  due: DayString;
  sales: number;
  input: number;
  status: 'open' | 'reported' | 'paid';
  paidAt?: DayString;
  salesCount: number;
  inputCount: number;
  salesBase: number;
  inputBase: number;
  partial?: boolean;
}

const RAW_VAT: RawVat[] = [
  { id: '2025-09', label: 'ספט׳–אוק׳ 25', from: '2025-09-01', to: '2025-10-31', due: '2025-11-15', sales: 5040, input: 1991, status: 'paid', paidAt: '2025-11-15', salesCount: 41, inputCount: 36, salesBase: 28000, inputBase: 11061 },
  { id: '2025-11', label: 'נוב׳–דצמ׳ 25', from: '2025-11-01', to: '2025-12-31', due: '2026-01-15', sales: 5958, input: 2407, status: 'paid', paidAt: '2026-01-15', salesCount: 43, inputCount: 39, salesBase: 33100, inputBase: 13372 },
  { id: '2026-01', label: 'ינו׳–פבר׳ 26', from: '2026-01-01', to: '2026-02-28', due: '2026-03-15', sales: 5904, input: 2203, status: 'paid', paidAt: '2026-03-15', salesCount: 42, inputCount: 38, salesBase: 32800, inputBase: 12239 },
  { id: '2026-03', label: 'מרץ–אפר׳ 26', from: '2026-03-01', to: '2026-04-30', due: '2026-05-15', sales: 6282, input: 2354, status: 'paid', paidAt: '2026-05-15', salesCount: 44, inputCount: 40, salesBase: 34900, inputBase: 13078 },
  { id: '2026-05', label: 'מאי–יוני 26', from: '2026-05-01', to: '2026-06-30', due: '2026-07-15', sales: 7398, input: 2783, status: 'paid', paidAt: '2026-07-15', salesCount: 45, inputCount: 41, salesBase: 41100, inputBase: 15461 },
  { id: '2026-07', label: 'יולי–אוג׳ 26', from: '2026-07-01', to: '2026-08-31', due: '2026-09-15', sales: 7686, input: 2879, status: 'open', salesCount: 44, inputCount: 41, salesBase: 42700, inputBase: 15993 },
  { id: '2026-09', label: 'ספט׳–אוק׳ 26', from: '2026-09-01', to: '2026-10-31', due: '2026-11-15', sales: 1557, input: 394, status: 'open', salesCount: 9, inputCount: 6, salesBase: 8650, inputBase: 2189, partial: true },
];

/** Local edits: expenses the owner marked as "יש חשבונית" credit their VAT period. */
const vatInputBonus = new Map<string, number>();

interface RawMissing {
  txId: string;
  name: string;
  day: DayString;
  category: ExpenseCategory;
  net: number;
  vat: number;
}

const RAW_MISSING: RawMissing[] = [
  { txId: 't17', name: 'Bug — כבלים ומתאמים', day: '2026-08-27', category: 'EQUIPMENT', net: 349, vat: 63 },
  { txId: 't32', name: 'Ivory', day: '2026-08-12', category: 'EQUIPMENT', net: 291, vat: 52 },
  { txId: 'x01', name: 'ACE כלי עבודה', day: '2026-07-09', category: 'EQUIPMENT', net: 600, vat: 108 },
];
let missingInvoices: RawMissing[] = RAW_MISSING.slice();

interface RawTaxEvent {
  id: string;
  type: 'vat' | 'income_tax_advance' | 'national_insurance';
  due: DayString;
  amount: number;
  label: string;
  basis: string;
  estimated: boolean;
  vatPeriod?: string;
}

const RAW_TAX_EVENTS: RawTaxEvent[] = [
  { id: 'te-vat-2026-07', type: 'vat', due: '2026-09-15', amount: 4807, label: 'מע"מ יולי–אוג׳', basis: 'אומדן', estimated: false, vatPeriod: '2026-07' },
  { id: 'te-adv-2026-09', type: 'income_tax_advance', due: '2026-09-15', amount: 1266, label: 'מקדמת מס הכנסה', basis: '6% × ₪21,100 (מחזור אוגוסט)', estimated: false },
  { id: 'te-ni-2026-09', type: 'national_insurance', due: '2026-09-15', amount: 1180, label: 'ביטוח לאומי', basis: 'סכום קבוע', estimated: false },
  { id: 'te-adv-2026-10', type: 'income_tax_advance', due: '2026-10-15', amount: 1260, label: 'מקדמת מס הכנסה', basis: '6% × ספטמבר', estimated: true },
  { id: 'te-ni-2026-10', type: 'national_insurance', due: '2026-10-15', amount: 1180, label: 'ביטוח לאומי', basis: 'סכום קבוע', estimated: false },
  { id: 'te-vat-2026-09', type: 'vat', due: '2026-11-15', amount: 4450, label: 'מע"מ ספט׳–אוק׳', basis: 'אומדן', estimated: true, vatPeriod: '2026-09' },
];

let taxSettings: TaxSettings = {
  businessKind: 'licensed',
  vatRates: [{ from: '2025-01-01', rate: VAT_RATE }],
  vatFrequency: 'bimonthly',
  vatBasis: 'cash',
  reportDay: 15,
  advanceRate: 0.06,
  niMonthlyAgorot: ILS(1180),
  ownerDrawAgorot: ILS(5000),
  ownerDrawDay: 2,
  forecastFloorAgorot: ILS(5000),
};

// ───────────────────────────── forecast (spec §7.4) ─────────────────────────────

const FORECAST_LINES: ForecastLine[] = [
  { key: 'balance', label: 'יתרה זמינה היום', whenLabel: '6.9', day: TODAY, amountAgorot: AVAILABLE, refs: [] },
  { key: 'invoices_due', label: 'חשבוניות עסקה פתוחות במועד (0401–0405 ב-1.10, 0409 ב-3.10)', whenLabel: '1.10, 3.10', day: '2026-10-01', amountAgorot: ILS(9027), refs: ['2026-0401', '2026-0402', '2026-0403', '2026-0404', '2026-0405', '2026-0409'] },
  { key: 'standing_orders', label: 'הוראות קבע HYP — חיוב 1.10, סליקה לבנק 6.10', whenLabel: '6.10', day: '2026-10-06', amountAgorot: ILS(3658), refs: [] },
  { key: 'variable_income', label: 'הכנסה משתנה צפויה (מרחוק + שטח, ממוצע יוני–אוג׳ × 1.18)', whenLabel: 'פרוס', day: null, amountAgorot: ILS(14081), refs: [] },
  { key: 'card_charge', label: 'חיוב כרטיס ****7712 — מחזור אוגוסט', whenLabel: '10.9', day: '2026-09-10', amountAgorot: ILS(-9520), refs: [] },
  { key: 'tax', label: 'מע"מ יולי–אוג׳ + מקדמה + ביטוח לאומי', whenLabel: '15.9', day: '2026-09-15', amountAgorot: ILS(-7253), refs: [] },
  { key: 'suppliers_bank', label: 'ספקים בערוץ בנק: רו"ח שמעוני · HYP עמלות · בזק', whenLabel: '25.9–3.10', day: '2026-09-25', amountAgorot: ILS(-860), refs: [] },
  { key: 'contractors', label: 'קבלן (העברה בנקאית, ממוצע יוני–אוג׳, עוסק פטור)', whenLabel: '~25.9', day: '2026-09-25', amountAgorot: ILS(-2667), refs: [] },
  { key: 'owner_draw', label: 'משיכת בעלים (₪5,000 ביום 2)', whenLabel: '2.10', day: '2026-10-02', amountAgorot: ILS(-5000), refs: [] },
];

const HORIZONS = [
  { days: 30 as const, date: '2026-10-06', balance: ILS(39886) },
  { days: 60 as const, date: '2026-11-05', balance: ILS(41159) },
  { days: 90 as const, date: '2026-12-05', balance: ILS(41640) },
];

const FORECAST_EVENT_ROWS: Array<[DayString, number, string, ForecastEvent['kind']]> = [
  ['2026-09-10', -9520, 'כרטיס', 'card'],
  ['2026-09-15', -7253, 'מע"מ + מקדמות', 'tax'],
  ['2026-09-25', -531, 'רו"ח', 'supplier'],
  ['2026-09-25', -2667, 'קבלן', 'supplier'],
  ['2026-10-01', 7906, 'חשבוניות', 'invoice'],
  ['2026-10-01', -223, 'בזק', 'supplier'],
  ['2026-10-02', -5000, 'משיכת בעלים', 'owner_draw'],
  ['2026-10-03', 1121, 'חשבונית 0409', 'invoice'],
  ['2026-10-03', -106, 'HYP עמלות', 'supplier'],
  ['2026-10-06', 3658, 'הו"ק', 'standing_order'],
  ['2026-10-10', -9747, 'כרטיס', 'card'],
  ['2026-10-15', -2440, 'מקדמה + ב"ל', 'tax'],
  ['2026-10-25', -531, 'רו"ח', 'supplier'],
  ['2026-10-25', -2667, 'קבלן', 'supplier'],
  ['2026-11-01', 7906, 'חשבוניות', 'invoice'],
  ['2026-11-01', -223, 'בזק', 'supplier'],
  ['2026-11-02', -5000, 'משיכת בעלים', 'owner_draw'],
  ['2026-11-03', -106, 'HYP עמלות', 'supplier'],
  ['2026-11-06', 3658, 'הו"ק', 'standing_order'],
  ['2026-11-10', -9747, 'כרטיס', 'card'],
  ['2026-11-15', -6890, 'מע"מ + מקדמה + ב"ל', 'tax'],
  ['2026-11-25', -531, 'רו"ח', 'supplier'],
  ['2026-11-25', -2667, 'קבלן', 'supplier'],
  ['2026-12-01', 7906, 'חשבוניות', 'invoice'],
  ['2026-12-01', -223, 'בזק', 'supplier'],
  ['2026-12-02', -5000, 'משיכת בעלים', 'owner_draw'],
  ['2026-12-03', -106, 'HYP עמלות', 'supplier'],
];

const FORECAST_EVENTS: ForecastEvent[] = FORECAST_EVENT_ROWS.map(([day, amount, label, kind]) => ({ day, kind, label, amountAgorot: ILS(amount) }));

/** Daily balance: actual 7.8 → 6.9 from the bank rows, forecast 7.9 → 5.12 from the events. */
function buildBalanceSeries(): BalancePoint[] {
  const out: BalancePoint[] = [];
  const actualByDay = new Map<string, number>();
  for (const raw of RAW_TX) {
    if (raw.account !== 'checking' || raw.status === 'PENDING') continue;
    actualByDay.set(raw.date, (actualByDay.get(raw.date) ?? 0) + raw.amount);
  }
  let balance = 29658; // 31.7 close (spec §7.4b)
  for (let i = 1; i <= 37; i++) {
    const day = addDays('2026-07-31', i);
    balance += actualByDay.get(day) ?? 0;
    if (i >= 7) out.push({ day, balanceAgorot: ILS(Math.round(balance)), actual: true });
  }
  const byDay = new Map<string, number>();
  for (const [day, amount] of FORECAST_EVENT_ROWS) byDay.set(day, (byDay.get(day) ?? 0) + amount);
  let forecast = 38420;
  const perDay = 14081 / 30;
  for (let i = 1; i <= 90; i++) {
    const day = addDays(TODAY, i);
    forecast += perDay + (byDay.get(day) ?? 0);
    out.push({ day, balanceAgorot: ILS(Math.round(forecast)), actual: false });
  }
  return out;
}
const BALANCE_SERIES = buildBalanceSeries();

// ───────────────────────────── alerts (spec §7.11) ─────────────────────────────

interface RawAlert {
  id: string;
  type: AlertType;
  severity: 'critical' | 'warn' | 'info';
  createdAt: string;
  readAt: string | null;
  dismissedAt?: string;
  title: string;
  body: string;
  link: string;
  action: string;
  entityType: Alert['entityType'];
  entityId: string | null;
  amount?: number;
  resolvedNote?: string;
}

const RAW_ALERTS: RawAlert[] = [
  { id: 'a1', type: 'INVOICE_OVERDUE', severity: 'critical', createdAt: '2026-09-01T06:15:00+03:00', readAt: null, title: '6 חשבוניות באיחור — ₪8,496', body: 'הוותיקה: מסעדת הגפן, 94 ימים. ההתראה התחדשה כש-2026-0381 עברה לאיחור.', link: '/invoices?status=overdue', action: 'לחשבוניות', entityType: 'invoice', entityId: null, amount: 8496 },
  { id: 'a2', type: 'EXPENSE_NO_INVOICE', severity: 'warn', createdAt: '2026-09-06T06:15:00+03:00', readAt: null, title: '3 הוצאות ללא חשבונית — ₪223 מע"מ שלא יקוזז', body: 'Bug — כבלים ומתאמים (27.8), Ivory (12.8), ACE כלי עבודה (9.7). סיכום שבועי.', link: '/expenses?noinvoice=1', action: 'להוצאות', entityType: 'expense', entityId: null, amount: 223 },
  { id: 'a3', type: 'TAX_ADVANCE_DUE', severity: 'warn', createdAt: '2026-09-05T06:15:00+03:00', readAt: null, title: 'מקדמת מס הכנסה ₪1,266 וביטוח לאומי ₪1,180 לתשלום עד 15.9', body: 'המקדמה מחושבת 6% ממחזור אוגוסט (₪21,100). סה"כ עם המע"מ: ₪7,253.', link: '/vat', action: 'למע"מ', entityType: 'tax_event', entityId: 'te-adv-2026-09', amount: 2446 },
  { id: 'a4', type: 'VAT_DUE', severity: 'warn', createdAt: '2026-09-01T06:15:00+03:00', readAt: null, title: 'דיווח מע"מ יולי–אוגוסט עד 15.9 — נותרו 9 ימים. אומדן לתשלום ₪4,807', body: 'מע"מ עסקאות ₪7,686 פחות תשומות ₪2,879 (בסיס מזומן).', link: '/vat?period=2026-07', action: 'למע"מ', entityType: 'vat_period', entityId: '2026-07', amount: 4807 },
  { id: 'a5', type: 'CATEGORY_OVER_BASELINE', severity: 'warn', createdAt: '2026-07-31T06:15:00+03:00', readAt: '2026-07-31T09:02:00+03:00', title: 'ציוד וחלפים: ₪4,500 ביולי — 29% מעל הבסיס (₪3,500)', body: 'הבסיס = ממוצע אפריל–יוני. אפשר לערוך את סף ההתראה לקטגוריה.', link: '/expenses?cat=EQUIPMENT', action: 'להוצאות', entityType: 'category', entityId: 'EQUIPMENT', amount: 450000 / 100 },
  { id: 'a6', type: 'RECEIPT_UNMATCHED', severity: 'info', createdAt: '2026-08-31T06:15:00+03:00', readAt: '2026-08-31T08:40:00+03:00', title: 'תקבול ₪1,003 מ-28.8 (ב. ברק) לא הותאם לחשבונית', body: 'התקבול הגיע אחרי חלון ההתאמה האוטומטי של 2026-0344 ובשם משלם שונה.', link: '/cashflow?match=unmatched', action: 'לתזרים', entityType: 'transaction', entityId: 't16', amount: 1003 },
  { id: 'a7', type: 'SUBSCRIPTION_CANCELLED', severity: 'info', createdAt: '2026-08-01T06:15:00+03:00', readAt: '2026-08-01T07:30:00+03:00', title: 'טכנו-סטור ביטל מנוי (₪600/חודש). MRR: ₪9,800', body: 'המנוי הסתיים ב-31.7. נטישה 12 חודשים: 11%.', link: '/ops', action: 'לתפעול', entityType: 'subscription', entityId: 's09', amount: 600 },
  { id: 'a8', type: 'CARD_CHARGE_SOON', severity: 'info', createdAt: '2026-08-08T06:15:00+03:00', readAt: '2026-08-08T07:00:00+03:00', dismissedAt: '2026-08-10T06:00:00+03:00', title: 'חיוב כרטיס עסקי ****7712 ב-10.8 — ₪10,170 (מחזור יולי). יש כיסוי', body: 'יתרה זמינה ביום ההתראה ₪38,146.', link: '/cashflow', action: 'לתזרים', entityType: 'card', entityId: CARD_REF.id, amount: 10170, resolvedNote: 'החיוב ירד 10.8' },
  { id: 'a9', type: 'VAT_DUE', severity: 'warn', createdAt: '2026-07-01T06:15:00+03:00', readAt: '2026-07-01T08:10:00+03:00', dismissedAt: '2026-07-15T11:00:00+03:00', title: 'דיווח מע"מ מאי–יוני עד 15.7 — ₪4,615', body: 'דווח ושולם ב-15.7.', link: '/vat?period=2026-05', action: 'למע"מ', entityType: 'vat_period', entityId: '2026-05', amount: 4615, resolvedNote: 'דווח ושולם 15.7' },
];

const alertState = new Map<string, { readAt: string | null; dismissedAt: string | null }>(
  RAW_ALERTS.map((a) => [a.id, { readAt: a.readAt, dismissedAt: a.dismissedAt ?? null }]),
);

function toAlert(raw: RawAlert): Alert {
  const state = alertState.get(raw.id);
  return {
    id: raw.id,
    type: raw.type,
    severity: raw.severity,
    title: raw.title,
    body: raw.body,
    entityType: raw.entityType,
    entityId: raw.entityId,
    amountAgorot: raw.amount == null ? null : ILS(raw.amount),
    link: raw.link,
    actionLabel: raw.action,
    createdAt: raw.createdAt,
    readAt: state?.readAt ?? null,
    dismissedAt: state?.dismissedAt ?? null,
    resolved: Boolean(state?.dismissedAt),
    resolvedNote: raw.resolvedNote ?? null,
  };
}

function alertsList(): Alert[] {
  return RAW_ALERTS.map(toAlert);
}
function severityRank(s: Alert['severity']): number {
  return s === 'critical' ? 3 : s === 'warn' ? 2 : 1;
}
function activeAlerts(): Alert[] {
  return alertsList()
    .filter((a) => !a.dismissedAt)
    .sort((a, b) => severityRank(b.severity) - severityRank(a.severity) || b.createdAt.localeCompare(a.createdAt));
}
function unreadCount(): number {
  return alertsList().filter((a) => !a.dismissedAt && !a.readAt).length;
}

// ───────────────────────────── period + freshness ─────────────────────────────

function makePeriod(param: PeriodParam | undefined): Period {
  const asOfMonth = monthOf(TODAY);
  let key: Period['key'] = 'month';
  let months: MonthString[] = [asOfMonth];
  let from = monthStart(asOfMonth);
  let to = TODAY;

  if (param === 'prev') {
    key = 'prev';
    months = [addMonths(asOfMonth, -1)];
    from = monthStart(months[0]!);
    to = monthEnd(months[0]!);
  } else if (param === '3m' || param === '12m') {
    key = param;
    const n = param === '3m' ? 3 : 12;
    months = [];
    for (let i = n - 1; i >= 0; i--) months.push(addMonths(asOfMonth, -i));
    from = monthStart(months[0]!);
    to = TODAY;
  } else if (typeof param === 'string' && /^\d{4}-\d{2}$/.test(param)) {
    key = 'month';
    months = [param];
    from = monthStart(param);
    to = param === asOfMonth ? TODAY : monthEnd(param);
  } else if (typeof param === 'string' && param.includes(',')) {
    key = 'custom';
    const [a, b] = param.split(',') as [DayString, DayString];
    from = a;
    to = b > TODAY ? TODAY : b;
    months = [];
    let m = monthOf(from);
    while (m <= monthOf(to)) {
      months.push(m);
      m = addMonths(m, 1);
    }
  }

  const lastMonth = months[months.length - 1] ?? asOfMonth;
  const [y, mm] = parts(to);
  const dim = daysInMonth(y, mm);
  const dayOfMonth = parts(to)[2];
  const partial = to < monthEnd(lastMonth);
  const idx = monthIndex(lastMonth);
  const prevMonth = idx > 0 ? RAW_MONTHS[idx - 1]!.ym : null;
  const prev = prevMonth
    ? partial
      ? { from: monthStart(prevMonth), to: `${prevMonth}-${String(dayOfMonth).padStart(2, '0')}` }
      : { from: monthStart(prevMonth), to: monthEnd(prevMonth) }
    : null;

  return {
    key,
    from,
    to,
    months,
    lastMonth,
    lastFrom: monthStart(lastMonth),
    lastTo: to,
    multi: months.length > 1,
    partial,
    isMonth: months.length === 1,
    prev,
    historyFrom: HISTORY_FROM,
    dayOfMonth,
    daysInMonth: dim,
  };
}

const freshness: Freshness = {
  lastFetchedAt: LAST_FETCH,
  ageHours: 3.2,
  level: 'fresh',
  staleHours: 36,
  criticalHours: 72,
  asOf: TODAY,
};

const sources: SourceFreshness[] = [
  { source: 'financy', lastFetchedAt: LAST_FETCH, ageHours: 3.2, level: 'fresh', configured: true, error: null, mode: 'live' },
  { source: 'hyp', lastFetchedAt: '2026-09-06T05:00:00+03:00', ageHours: 4.4, level: 'fresh', configured: true, error: null, mode: 'demo' },
  { source: 'labtop', lastFetchedAt: null, ageHours: null, level: 'none', configured: false, error: null, mode: 'demo' },
];

const USER: User = {
  id: 'u1',
  email: 'yair@labtop.co.il',
  displayName: 'יאיר',
  role: 'owner',
  createdAt: '2026-09-01T00:00:00.000Z',
  lastLoginAt: SERVER_TIME,
};

// ───────────────────────────── derivations ─────────────────────────────

function openInvoices(): Invoice[] {
  return INVOICES.filter((i) => i.status === 'open' || i.status === 'overdue');
}
function overdueInvoices(): Invoice[] {
  return INVOICES.filter((i) => i.status === 'overdue');
}
function sum(values: number[]): number {
  return values.reduce((a, b) => a + b, 0);
}
function agingStats(): AgingBucketStat[] {
  const buckets: AgingBucket[] = ['0-30', '31-60', '61-90', '90+'];
  return buckets.map((bucket) => {
    const rows = overdueInvoices().filter((i) => i.agingBucket === bucket);
    return { bucket, agorot: sum(rows.map((i) => i.grossAgorot)), count: rows.length };
  });
}
function customerBalances(): CustomerBalance[] {
  const map = new Map<string, CustomerBalance>();
  for (const inv of openInvoices()) {
    const raw = CUSTOMER_BY_ID.get(inv.customerId);
    const entry = map.get(inv.customerId) ?? {
      customerId: inv.customerId,
      customerName: inv.customerName,
      openAgorot: 0,
      overdueAgorot: 0,
      overdueCount: 0,
      avgDaysToPay: raw?.avgDays ?? null,
      method: raw?.method ?? 'transfer',
      hasStandingOrder: raw?.method === 'standing_order',
    };
    entry.openAgorot += inv.grossAgorot;
    if (inv.status === 'overdue') {
      entry.overdueAgorot += inv.grossAgorot;
      entry.overdueCount += 1;
    }
    map.set(inv.customerId, entry);
  }
  // The server sorts by OVERDUE first (business/server/metrics/receivables.ts) and the narrative's
  // collection sentence needs `topDebtors[0].overdueAgorot > 0`. Sorting by `openAgorot` here made
  // the only `עכשיו` fact — the overdue balance and who owes it — vanish from `מה קורה`.
  return [...map.values()].sort((a, b) =>
    b.overdueAgorot !== a.overdueAgorot ? b.overdueAgorot - a.overdueAgorot : b.openAgorot - a.openAgorot,
  );
}
function collectedThisMonth(): { agorot: number; count: number } {
  const rows = allTx().filter((t) => t.kind === 'CHECKING' && (t.amountAgorot ?? 0) > 0 && t.match && t.effDate >= monthStart(monthOf(TODAY)));
  return { agorot: sum(rows.map((t) => t.amountAgorot ?? 0)), count: rows.length };
}
function baselineFor(index: number): number[] {
  const prev = RAW_MONTHS.slice(Math.max(0, index - 3), index);
  return CATS.map((_c, k) => (prev.length ? Math.round(sum(prev.map((m) => m.exp[k] ?? 0)) / prev.length) : 0));
}
function baselineMonthsFor(index: number): MonthString[] {
  return RAW_MONTHS.slice(Math.max(0, index - 3), index).map((m) => m.ym);
}
/** Fixed-supplier categories prorate by the suppliers' charge calendar, not by day count (§4.5). */
function proratedBaseline(catIndex: number, base: number, dayOfMonth: number, dim: number, partial: boolean): number {
  if (!partial) return base;
  const cat = CATS[catIndex]!;
  if (cat === 'SOFTWARE' || cat === 'TELECOM' || cat === 'BOOKKEEPING') {
    return sum(SUPPLIERS.filter((s) => s.category === cat && (s.chargeDay ?? 99) <= dayOfMonth).map((s) => s.expectedMonthlyNetAgorot)) / 100;
  }
  return Math.round((base * dayOfMonth) / dim);
}

const categoryThresholds = new Map<ExpenseCategory, number | null>();
function thresholdFor(cat: ExpenseCategory): number {
  const value = categoryThresholds.get(cat);
  return value === undefined ? 20 : value === null ? Number.POSITIVE_INFINITY : value;
}

function categoryStats(index: number, period: Period): ExpenseCategoryStat[] {
  const m = RAW_MONTHS[index]!;
  const base = baselineFor(index);
  const months = baselineMonthsFor(index);
  return CATS.map((cat, k) => {
    const amount = ILS(m.exp[k] ?? 0);
    const baseline = ILS(base[k] ?? 0);
    const prorated = ILS(proratedBaseline(k, base[k] ?? 0, period.dayOfMonth, period.daysInMonth, Boolean(m.partial)));
    const compare = m.partial ? prorated : baseline;
    const ratio = compare > 0 ? amount / compare : null;
    const threshold = thresholdFor(cat);
    const overPct = ratio == null ? 0 : (ratio - 1) * 100;
    const over = ratio != null && overPct > threshold;
    return {
      category: cat,
      amountAgorot: amount,
      monthAmountAgorot: amount,
      baselineAgorot: baseline,
      baselineMonths: months,
      baselinePartial: months.length < 3,
      proratedBaselineAgorot: prorated,
      ratio,
      thresholdPct: threshold === Number.POSITIVE_INFINITY ? 0 : threshold,
      over,
      severity: over ? (overPct > 50 ? 'critical' : 'warn') : null,
      txCount: allTx().filter((t) => t.expense?.category === cat && monthOf(t.effDate) === m.ym).length,
    };
  });
}

function expenseMonths(endIndex: number, count = 6): ExpenseMonth[] {
  return RAW_MONTHS.slice(Math.max(0, endIndex - count + 1), endIndex + 1).map((m) => {
    const byCategory = {} as Record<ExpenseCategory, number>;
    CATS.forEach((cat, k) => {
      byCategory[cat] = ILS(m.exp[k] ?? 0);
    });
    return { month: m.ym, byCategory, totalAgorot: ILS(expenseOf(m)), partial: Boolean(m.partial) };
  });
}

function vatPeriodRef(raw: RawVat): VatPeriodRef {
  return { id: raw.id, label: raw.label, from: raw.from, to: raw.to, dueDate: raw.due, status: raw.status, partial: Boolean(raw.partial) };
}

function unpaidVatIn(raw: RawVat): { count: number; agorot: number } {
  const rows = INVOICES.filter((i) => i.docType === 'proforma' && (i.status === 'open' || i.status === 'overdue') && i.issueDate >= raw.from && i.issueDate <= raw.to);
  return { count: rows.length, agorot: sum(rows.map((i) => i.vatAgorot)) };
}

function nonDeductibleIn(raw: RawVat): { count: number; agorot: number } {
  const rows = missingInvoices.filter((x) => x.day >= raw.from && x.day <= raw.to);
  return { count: rows.length, agorot: ILS(sum(rows.map((x) => x.vat))) };
}

function toVatPeriod(raw: RawVat): VatPeriod {
  const bonus = vatInputBonus.get(raw.id) ?? 0;
  const input = ILS(raw.input) + bonus;
  const output = ILS(raw.sales);
  const unpaid = unpaidVatIn(raw);
  const nonDeductible = nonDeductibleIn(raw);
  return {
    ...vatPeriodRef(raw),
    calc: {
      basis: taxSettings.vatBasis,
      rate: VAT_RATE,
      outputVatAgorot: output,
      outputCount: raw.salesCount,
      outputBaseAgorot: ILS(raw.salesBase),
      creditsAgorot: 0,
      inputVatAgorot: input,
      inputCount: raw.inputCount + (bonus ? 1 : 0),
      inputBaseAgorot: ILS(raw.inputBase),
      netVatAgorot: output - input,
      nonDeductibleVatAgorot: nonDeductible.agorot,
      nonDeductibleCount: nonDeductible.count,
      unpaidOutputVatAgorot: unpaid.agorot,
      unpaidCount: unpaid.count,
      adjustments: { vehicleTwoThirdsAgorot: ILS(917), exemptSupplierAgorot: ILS(5400), foreignAgorot: 0 },
    },
    reportedAt: raw.status === 'paid' ? raw.paidAt ?? null : null,
    paidAt: raw.paidAt ?? null,
    paidAgorot: raw.paidAt ? output - input : null,
    daysUntilDue: daysBetween(TODAY, raw.due),
  };
}

function taxEvents(maxDays?: number): TaxEvent[] {
  return RAW_TAX_EVENTS.filter((e) => {
    const d = daysBetween(TODAY, e.due);
    if (d < 0) return false;
    if (maxDays != null && d > maxDays) return false;
    if (e.vatPeriod) {
      const vp = RAW_VAT.find((v) => v.id === e.vatPeriod);
      if (vp && vp.status === 'paid') return false;
    }
    return true;
  }).map((e) => {
    const vp = e.vatPeriod ? RAW_VAT.find((v) => v.id === e.vatPeriod) : undefined;
    const amount = vp ? toVatPeriod(vp).calc.netVatAgorot : ILS(e.amount);
    return {
      id: e.id,
      type: e.type,
      dueDate: e.due,
      amountAgorot: amount,
      label: e.label,
      basisLabel: e.basis,
      estimated: e.estimated,
      daysUntilDue: daysBetween(TODAY, e.due),
      paidAt: null,
      vatPeriod: e.vatPeriod ?? null,
    };
  });
}

function vatTrend(): VatTrendPoint[] {
  return RAW_VAT.slice(0, 6).map((raw) => {
    const p = toVatPeriod(raw);
    return { period: raw.id, label: raw.label, outputVatAgorot: p.calc.outputVatAgorot, inputVatAgorot: p.calc.inputVatAgorot, netVatAgorot: p.calc.netVatAgorot, status: raw.status };
  });
}

function revenueByLine(endIndex: number, count: number): ServiceLineMonth[] {
  return RAW_MONTHS.slice(Math.max(0, endIndex - count + 1), endIndex + 1).map((m) => ({
    month: m.ym,
    byLine: { subscriptions: ILS(m.subs), field: ILS(m.field), remote: ILS(m.remote) },
    totalAgorot: ILS(incomeOf(m)),
    partial: Boolean(m.partial),
  }));
}

function mrrTrend(): MrrPoint[] {
  return RAW_MONTHS.slice(0, 12).map((m) => ({ month: m.ym, mrrAgorot: ILS(m.subs), activeCount: m.subsCount }));
}

function opsSummary(): OpsSummary {
  const active = SUBSCRIPTIONS.filter((s) => s.status === 'active');
  const rev12 = sum(RAW_MONTHS.slice(0, 12).map((m) => incomeOf(m)));
  return {
    source: { mode: 'demo', connected: false, fetchedAt: null, error: null },
    asOf: TODAY,
    calls: {
      open: CALLS.filter((c) => c.status !== 'done').length,
      inProgress: CALLS.filter((c) => c.status === 'in_progress').length,
      doneThisMonth: 9,
      avgCloseDays: 1.6,
      fromSentinelThisMonth: 5,
    },
    subscriptions: {
      active: active.length,
      paused: SUBSCRIPTIONS.filter((s) => s.status === 'paused').length,
      cancelled: SUBSCRIPTIONS.filter((s) => s.status === 'cancelled').length,
      mrrAgorot: sum(active.map((s) => s.monthlyNetAgorot)),
      churn12m: 0.11,
      churnLabel: '1 מ-9',
    },
    mrrTrend: mrrTrend(),
    revenueByLine: revenueByLine(11, 12),
    topCustomers: RAW_CUSTOMERS.filter((c) => c.rev12 > 0)
      .sort((a, b) => b.rev12 - a.rev12)
      .slice(0, 6)
      .map((c) => ({ customerId: c.id, customerName: c.name, revenue12mAgorot: ILS(c.rev12), share: c.rev12 / rev12 })),
  };
}

function toCustomer(raw: RawCustomer): Customer {
  const balance = customerBalances().find((b) => b.customerId === raw.id);
  const sub = SUBSCRIPTIONS.find((s) => s.customerId === raw.id && s.status === 'active');
  return {
    id: raw.id,
    name: raw.name,
    type: raw.type,
    email: raw.email,
    phone: null,
    paymentTerms: raw.method === 'transfer' ? 'net30' : 'immediate',
    paymentTermsDays: raw.method === 'transfer' ? 30 : 0,
    subscriptionId: sub?.id ?? null,
    openBalanceAgorot: balance?.openAgorot ?? 0,
    overdueAgorot: balance?.overdueAgorot ?? 0,
    overdueCount: balance?.overdueCount ?? 0,
    avgDaysToPay: raw.avgDays,
    revenue12mAgorot: ILS(raw.rev12),
    hasStandingOrder: raw.method === 'standing_order',
    source: 'labtop',
    updatedAt: LAST_FETCH,
  };
}

function cardCycleSummary(): SummaryResponse['card'] {
  return {
    account: CARD_REF,
    creditLimitAgorot: ILS(25000),
    utilization: 2465 / 25000,
    dueDay: 10,
    dueDaySource: 'financy',
    current: { from: '2026-09-01', to: '2026-09-30', dueDate: '2026-10-10', status: 'open', amountAgorot: ILS(2465), pendingAgorot: ILS(152), txCount: 6, matchedTxId: null, matchedDate: null },
    next: { from: '2026-08-01', to: '2026-08-31', dueDate: '2026-09-10', status: 'final', amountAgorot: ILS(9520), pendingAgorot: 0, txCount: 21, matchedTxId: null, matchedDate: null },
    averageAgorot: ILS(9747),
    recent: [
      { from: '2026-07-01', to: '2026-07-31', dueDate: '2026-08-10', status: 'charged', amountAgorot: ILS(10170), pendingAgorot: 0, txCount: 22, matchedTxId: 't34', matchedDate: '2026-08-10' },
      { from: '2026-06-01', to: '2026-06-30', dueDate: '2026-07-10', status: 'charged', amountAgorot: ILS(9550), pendingAgorot: 0, txCount: 20, matchedTxId: null, matchedDate: '2026-07-10' },
    ],
  };
}

function currentVatRaw(): RawVat {
  return RAW_VAT.find((v) => v.status !== 'paid') ?? RAW_VAT[RAW_VAT.length - 1]!;
}

// ───────────────────────────── REDESIGN §2/§3 (the shipped code, not a preview of its own) ────────

/**
 * The demo's `InsightInput`, fed to `business/server/insight.ts` — the same functions the server
 * calls, so `?mock=1` renders the SHIPPED narrative and urgency feed.
 *
 * This is deliberate and it is the point: when the mock built neither field, `insightOf()` fell
 * through to its browser-side reconstruction and the preview showed a second, divergent product
 * (row titles off `event.label` instead of `חיוב כרטיס {name}`, no collection sentence). A design
 * sign-off has to be a sign-off on what ships.
 */
function insightInput(): InsightInput {
  const vpRaw = currentVatRaw();
  const vp = toVatPeriod(vpRaw);
  const card = cardCycleSummary();
  const missingRows = allTx().filter((t) => t.expense && !t.expense.hasTaxInvoice && !t.expense.foreign && monthOf(t.effDate) === monthOf(TODAY));
  const index = Math.max(0, monthIndex(monthOf(TODAY)));
  const m = RAW_MONTHS[index]!;
  return {
    data: {
      asOf: TODAY,
      // `InvoiceView` is the server's row; the wire `Invoice` carries everything but the three
      // fields HYP supplies and the wire drops. The demo has one currency and no credit notes.
      invoices: INVOICES.map((i) => ({ ...i, currency: 'ILS', creditsNumber: null, batchId: null })),
      accounts: ACCOUNTS.map((a) => ({ id: a.id, connectionId: a.connectionId, assigned: a.assigned })),
      connections: CONNECTIONS.map((c) => ({ id: c.id, provider_id: c.providerId, status: c.status, expiry_date: c.expiryDate })),
      freshness: { level: freshness.level },
    },
    month: { incomeNetAgorot: ILS(incomeOf(m)), expenseNetAgorot: ILS(expenseOf(m)), profitAgorot: ILS(incomeOf(m) - expenseOf(m)) },
    collection: {
      openAgorot: sum(openInvoices().map((i) => i.grossAgorot)),
      overdueAgorot: sum(overdueInvoices().map((i) => i.grossAgorot)),
      topDebtors: customerBalances().map((b) => ({ customerId: b.customerId, customerName: b.customerName, overdueAgorot: b.overdueAgorot })),
    },
    tax: { period: { id: vpRaw.id, label: vpRaw.label, status: vpRaw.status }, dueDate: vp.dueDate, netVatAgorot: vp.calc.netVatAgorot },
    deadlines: taxEvents(),
    card: card === null ? null : { account: { id: CARD_REF.id, name: CARD_REF.name, providerId: CARD_REF.providerId }, next: card.next },
    forecast: {
      computable: true,
      floorAgorot: taxSettings.forecastFloorAgorot,
      series: BALANCE_SERIES,
      events: FORECAST_EVENTS,
      horizons: HORIZONS.map((h) => ({ balanceAgorot: h.balance })),
    },
    missingInvoices: {
      count: missingRows.length,
      grossAgorot: sum(missingRows.map((t) => t.expense!.grossAgorot)),
      vatAgorot: sum(missingRows.map((t) => t.expense!.vatAgorot)),
    },
    lastSync: { ok: 1, error: null },
  };
}

// ───────────────────────────── the adapter ─────────────────────────────

const DELAY = 120;
function later<T>(value: T, ms = DELAY): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

let alertsListeners: Array<(event: import('../../api-types.ts').SseEvent) => void> = [];
function emit(event: import('../../api-types.ts').SseEvent): void {
  for (const listener of alertsListeners) listener(event);
}

export function createMockApi(): Api {
  let settings: SettingsResponse = {
    alerts: {
      invoiceOverdueDays: 1,
      invoiceOverdueRepeatDays: 14,
      vatWarnDays: 14,
      vatCriticalDays: 3,
      taxAdvanceDays: 10,
      cardChargeDays: 2,
      categoryThresholdPct: 20,
      categoryMinDayOfMonth: 7,
      receiptUnmatchedDays: 3,
      expenseNoInvoiceMinAgorot: ILS(100),
      expenseNoInvoiceDays: 7,
      staleHours: 36,
      disabled: [],
    },
    alertDefaults: {
      invoiceOverdueDays: 1,
      invoiceOverdueRepeatDays: 14,
      vatWarnDays: 14,
      vatCriticalDays: 3,
      taxAdvanceDays: 10,
      cardChargeDays: 2,
      categoryThresholdPct: 20,
      categoryMinDayOfMonth: 7,
      receiptUnmatchedDays: 3,
      expenseNoInvoiceMinAgorot: ILS(100),
      expenseNoInvoiceDays: 7,
      staleHours: 36,
      disabled: [],
    },
    appearance: { theme: 'auto', density: 'normal', numberLocale: 'he-IL' },
    tax: taxSettings,
    plan: { tier: 'Starter', credits: 500 },
    sync: {
      everyMinutes: 360,
      lastRunAt: LAST_FETCH,
      nextRunAt: '2026-09-06T12:12:00+03:00',
      sourceMinutes: { financy: 360, hyp: 60, labtop: 15 },
    },
    integrations: {
      financy: { configured: true, hint: 'discount', lastCheckedAt: LAST_FETCH, lastCheckOk: true, lastError: null, mode: 'live' },
      hyp: { configured: true, hint: '···2345', lastCheckedAt: '2026-09-06T05:00:00+03:00', lastCheckOk: true, lastError: null, mode: 'demo' },
      labtop: { configured: false, hint: 'https://api.labtop.co.il', lastCheckedAt: null, lastCheckOk: null, lastError: null, mode: 'demo' },
    },
    users: 1,
  };

  const syncRun: SyncRun = {
    id: 41,
    kind: 'scheduled',
    sources: ['financy', 'hyp'],
    startedAt: '2026-09-06T06:11:00+03:00',
    finishedAt: LAST_FETCH,
    ok: true,
    requestedBy: null,
    inserted: 6,
    updated: 12,
    superseded: 0,
    invoices: 4,
    creditsUsed: 0,
    error: null,
  };

  function transactionsFor(query: TransactionsQuery): TransactionRow[] {
    const period = makePeriod(query.period);
    let rows = allTx();
    if (query.match === 'unmatched') {
      rows = rows.filter((t) => (t.amountAgorot ?? 0) > 0 && !t.match);
    } else {
      rows = rows.filter((t) => t.effDate >= (query.from ?? period.from) && t.effDate <= (query.to ?? period.to));
      if (query.match === 'matched') rows = rows.filter((t) => (t.amountAgorot ?? 0) > 0 && Boolean(t.match));
    }
    if (query.account && query.account !== 'all') rows = rows.filter((t) => (query.account === 'card' ? t.kind === 'CARD' : t.kind === 'CHECKING'));
    if (query.dir === 'in') rows = rows.filter((t) => (t.amountAgorot ?? 0) > 0);
    if (query.dir === 'out') rows = rows.filter((t) => (t.amountAgorot ?? 0) < 0);
    if (query.cat && query.cat !== 'all') rows = rows.filter((t) => t.expense?.category === query.cat);
    if (query.invoice === 'missing') rows = rows.filter((t) => t.expense && !t.expense.hasTaxInvoice && !t.expense.foreign);
    if (query.invoice === 'foreign') rows = rows.filter((t) => t.expense?.foreign);
    if (query.channel) rows = rows.filter((t) => t.expense?.paymentChannel === query.channel);
    if (query.q) {
      const q = query.q;
      rows = rows.filter((t) => (t.description + ' ' + (t.expense ? CATEGORY[t.expense.category].label : '')).includes(q));
    }
    rows.sort((a, b) => (query.sort === 'amount' ? Math.abs(b.amountAgorot ?? 0) - Math.abs(a.amountAgorot ?? 0) : b.effDate.localeCompare(a.effDate) || b.id.localeCompare(a.id)));
    return rows;
  }

  function invoicesFor(query: InvoicesQuery): Invoice[] {
    let rows = INVOICES.slice();
    if (query.status && query.status !== 'all') rows = rows.filter((i) => i.status === query.status);
    if (query.customer) rows = rows.filter((i) => i.customerId === query.customer);
    if (query.bucket) rows = rows.filter((i) => i.agingBucket === query.bucket);
    if (query.from) rows = rows.filter((i) => i.issueDate >= query.from!);
    if (query.to) rows = rows.filter((i) => i.issueDate <= query.to!);
    if (query.q) rows = rows.filter((i) => (i.number + ' ' + i.customerName).includes(query.q!));
    const sort = query.sort ?? 'issue';
    rows.sort((a, b) => {
      switch (sort) {
        case 'due':
          return a.dueDate.localeCompare(b.dueDate);
        case 'amount':
          return b.grossAgorot - a.grossAgorot;
        case 'customer':
          return a.customerName.localeCompare(b.customerName, 'he');
        default:
          return b.issueDate.localeCompare(a.issueDate) || b.number.localeCompare(a.number);
      }
    });
    return rows;
  }

  function invoiceDetail(number: string): InvoiceDetail {
    const invoice = INVOICES.find((i) => i.number === number);
    if (!invoice) throw new ApiError('NOT_FOUND', he.errors.NOT_FOUND, 404);
    const raw = CUSTOMER_BY_ID.get(invoice.customerId)!;
    const matchedTx = invoice.matchedTxId ? allTx().find((t) => t.id === invoice.matchedTxId) ?? null : null;
    const candidates = allTx()
      .filter((t) => (t.amountAgorot ?? 0) > 0 && !t.match)
      .map((t) => ({ txId: t.id, day: t.effDate, description: t.description, amountAgorot: t.amountAgorot ?? 0, deltaAgorot: (t.amountAgorot ?? 0) - invoice.grossAgorot }))
      .filter((c) => Math.abs(c.deltaAgorot) <= invoice.grossAgorot * 0.05)
      .sort((a, b) => Math.abs(a.deltaAgorot) - Math.abs(b.deltaAgorot));
    return {
      ...invoice,
      customer: toCustomer(raw),
      matchedTx,
      receiptCandidates: candidates,
      reminderHistory: invoice.reminders.map((day, i) => ({ id: `${invoice.number}-r${i}`, day, createdAt: `${day}T09:00:00+03:00`, createdBy: USER.displayName, via: 'copy' as const })),
    };
  }

  function summary(periodParam?: PeriodParam): SummaryResponse {
    const period = makePeriod(periodParam);
    const index = Math.max(0, monthIndex(period.lastMonth));
    const m = RAW_MONTHS[index]!;
    const prevRow = index > 0 ? RAW_MONTHS[index - 1]! : null;
    const monthFigures = {
      incomeNetAgorot: ILS(incomeOf(m)),
      expenseNetAgorot: ILS(expenseOf(m)),
      profitAgorot: ILS(incomeOf(m) - expenseOf(m)),
      invoiceCount: INVOICES.filter((i) => monthOf(i.issueDate) === m.ym).length,
      expenseCount: allTx().filter((t) => t.expense && monthOf(t.effDate) === m.ym).length,
    };
    const prevFigures = m.partial
      ? { incomeNetAgorot: ILS(AUG_1_6.income), expenseNetAgorot: ILS(AUG_1_6.expense), profitAgorot: ILS(AUG_1_6.profit), invoiceCount: AUG_1_6.invoices, expenseCount: AUG_1_6.expenses }
      : prevRow
        ? {
            incomeNetAgorot: ILS(incomeOf(prevRow)),
            expenseNetAgorot: ILS(expenseOf(prevRow)),
            profitAgorot: ILS(incomeOf(prevRow) - expenseOf(prevRow)),
            invoiceCount: 22,
            expenseCount: 21,
          }
        : null;

    const vpRaw = currentVatRaw();
    const vp = toVatPeriod(vpRaw);
    const upcoming = taxEvents(60);
    const nextDay = upcoming.length ? upcoming.map((e) => e.dueDate).sort()[0]! : null;
    const nextItems = nextDay ? upcoming.filter((e) => e.dueDate === nextDay) : [];
    const ops = opsSummary();
    const lineTotals = revenueByLine(index, 1)[0]?.byLine ?? { subscriptions: 0, field: 0, remote: 0 };
    const insight = insightInput();

    return {
      asOf: TODAY,
      serverTime: SERVER_TIME,
      period,
      state: 'ok',
      freshness,
      sources,
      connections: CONNECTIONS.map((c) => ({ id: c.id, providerId: c.providerId, status: c.status, lastFetchedAt: c.lastFetchedAt, expiryDate: c.expiryDate, maskedPans: c.maskedPans, needsRelink: c.needsRelink })),
      credits: { usedThisMonth: 40, plan: { tier: 'Starter', credits: 500 }, perRefresh: 20 },
      balance: {
        account: CHECKING_REF,
        availableAgorot: AVAILABLE,
        availableSource: 'interimAvailable',
        forecastAgorot: HORIZONS[0]!.balance,
        forecastDate: HORIZONS[0]!.date,
        forecastDeltaAgorot: HORIZONS[0]!.balance - AVAILABLE,
        forecastDeltaPct: ((HORIZONS[0]!.balance - AVAILABLE) / AVAILABLE) * 100,
        series: BALANCE_SERIES.slice(0, 61),
        events: FORECAST_EVENTS.filter((e) => e.day <= HORIZONS[0]!.date),
        note: he.home.cardOutOfWindow('10.10'),
      },
      month: monthFigures,
      monthPrev: prevFigures,
      collection: {
        openAgorot: sum(openInvoices().map((i) => i.grossAgorot)),
        openCount: openInvoices().length,
        overdueAgorot: sum(overdueInvoices().map((i) => i.grossAgorot)),
        overdueCount: overdueInvoices().length,
        aging: agingStats(),
        topDebtors: customerBalances().slice(0, 3),
      },
      tax: {
        period: vatPeriodRef(vpRaw),
        outputVatAgorot: vp.calc.outputVatAgorot,
        inputVatAgorot: vp.calc.inputVatAgorot,
        netVatAgorot: vp.calc.netVatAgorot,
        nonDeductibleVatAgorot: vp.calc.nonDeductibleVatAgorot,
        nonDeductibleCount: vp.calc.nonDeductibleCount,
        unpaidOutputVatAgorot: vp.calc.unpaidOutputVatAgorot,
        unpaidCount: vp.calc.unpaidCount,
        dueDate: vp.dueDate,
        daysUntilDue: vp.daysUntilDue,
        upcoming,
        nextDueTotalAgorot: sum(nextItems.map((e) => e.amountAgorot)),
        nextDueDate: nextDay,
      },
      ops: {
        source: ops.source,
        openCalls: ops.calls.open,
        inProgressCalls: ops.calls.inProgress,
        activeSubscriptions: ops.subscriptions.active,
        mrrAgorot: ops.subscriptions.mrrAgorot,
        mrrTrend: ops.mrrTrend,
        avgCloseDays: ops.calls.avgCloseDays,
        revenueByLine: lineTotals,
      },
      card: cardCycleSummary(),
      alerts: { openCount: activeAlerts().length, unreadCount: unreadCount(), items: activeAlerts().slice(0, 5) },
      recent: allTx()
        .sort((a, b) => b.effDate.localeCompare(a.effDate) || b.id.localeCompare(a.id))
        .slice(0, 5),
      revenueByLine: revenueByLine(index, 6),
      user: { id: USER.id, displayName: USER.displayName, role: USER.role },
      // REDESIGN §2/§3, straight from `business/server/insight.ts`.
      narrative: buildBusinessNarrative(insight),
      urgency: buildUrgency(insight),
    };
  }

  function cashflow(query?: { months?: number; period?: PeriodParam }): CashflowResponse {
    const period = makePeriod(query?.period);
    const endIndex = Math.max(0, monthIndex(period.lastMonth));
    const count = query?.months ?? 13;
    const window = RAW_MONTHS.slice(Math.max(0, endIndex - count + 1), endIndex + 1);
    const months: CashflowMonth[] = window.map((m) => ({
      month: m.ym,
      inAgorot: ILS(m.bankIn),
      outAgorot: ILS(m.bankOut),
      netAgorot: ILS(m.bankIn - m.bankOut),
      endBalanceAgorot: ILS(m.endBal),
      outBreakdown: {
        cardSettlementAgorot: ILS(m.card),
        taxAgorot: ILS(m.tax),
        contractorsAgorot: ILS(m.contractor),
        ownerDrawAgorot: ILS(m.partial ? 5000 : m.ownerDraw),
        otherAgorot: ILS(Math.max(0, m.bankOut - m.card - m.tax - m.contractor - (m.partial ? 5000 : m.ownerDraw))),
      },
      partial: Boolean(m.partial),
      txCount: allTx().filter((t) => t.kind === 'CHECKING' && monthOf(t.effDate) === m.ym).length,
    }));
    const full = months.filter((m) => !m.partial);
    return {
      asOf: TODAY,
      period,
      months,
      totals: {
        months: full.length,
        inAgorot: sum(full.map((m) => m.inAgorot)),
        outAgorot: sum(full.map((m) => m.outAgorot)),
        netAgorot: sum(full.map((m) => m.netAgorot)),
      },
      card: cardCycleSummary(),
      unmatchedReceipts: (() => {
        const rows = allTx().filter((t) => (t.amountAgorot ?? 0) > 0 && !t.match);
        return { count: rows.length, agorot: sum(rows.map((t) => t.amountAgorot ?? 0)) };
      })(),
    };
  }

  function forecast(): ForecastResponse {
    return {
      asOf: TODAY,
      availableAgorot: AVAILABLE,
      floorAgorot: taxSettings.forecastFloorAgorot,
      horizons: HORIZONS.map((h) => ({
        days: h.days,
        date: h.date,
        balanceAgorot: h.balance,
        deltaAgorot: h.balance - AVAILABLE,
        deltaPct: ((h.balance - AVAILABLE) / AVAILABLE) * 100,
        belowFloor: h.balance < taxSettings.forecastFloorAgorot,
      })),
      lines: FORECAST_LINES,
      exclusions: [
        { key: 'overdue_invoices', label: 'לא נכלל: חשבוניות באיחור', amountAgorot: sum(overdueInvoices().map((i) => i.grossAgorot)), count: overdueInvoices().length },
        { key: 'card_out_of_window', label: 'הוצאות הכרטיס של ספטמבר יורדות ב-10.10 — מחוץ לחלון', amountAgorot: ILS(9747), count: null },
      ],
      series: BALANCE_SERIES,
      events: FORECAST_EVENTS,
      assumptions: {
        variableIncomeMonthlyAgorot: ILS(14081),
        variableIncomeMonths: ['2026-06', '2026-07', '2026-08'],
        cardAverageAgorot: ILS(9747),
        contractorsMonthlyAgorot: ILS(2667),
        contractorsDay: 25,
        ownerDrawAgorot: taxSettings.ownerDrawAgorot,
        ownerDrawDay: taxSettings.ownerDrawDay,
        standingOrderSettlementLagDays: 5,
        vatBasis: taxSettings.vatBasis,
      },
      computable: true,
    };
  }

  function expenses(query: ExpensesQuery): ExpensesResponse {
    const period = makePeriod(query.period);
    const index = Math.max(0, monthIndex(period.lastMonth));
    const m = RAW_MONTHS[index]!;
    let rows = allTx().filter((t) => t.expense && monthOf(t.effDate) === m.ym);
    if (query.cat && query.cat !== 'all') rows = rows.filter((t) => t.expense?.category === query.cat);
    if (query.invoice === 'missing') rows = rows.filter((t) => t.expense && !t.expense.hasTaxInvoice && !t.expense.foreign);
    if (query.invoice === 'foreign') rows = rows.filter((t) => t.expense?.foreign);
    if (query.channel) rows = rows.filter((t) => (query.channel === 'card' ? t.kind === 'CARD' : t.kind === 'CHECKING'));
    if (query.q) rows = rows.filter((t) => t.description.includes(query.q!));
    rows.sort((a, b) => b.effDate.localeCompare(a.effDate));

    const items: ExpenseRow[] = rows.map((t) => ({
      txId: t.id,
      day: t.effDate,
      account: t.account,
      merchant: t.merchant ?? t.description,
      description: t.description,
      status: t.status,
      expense: t.expense!,
      cycleMonth: t.cycleMonth,
      note: t.note,
    }));

    const base = baselineFor(index);
    const baseTotal = sum(base);
    const prorated = m.partial ? Math.round((baseTotal * period.dayOfMonth) / period.daysInMonth) : baseTotal;
    const missingRows = allTx().filter((t) => t.expense && !t.expense.hasTaxInvoice && !t.expense.foreign && monthOf(t.effDate) === m.ym);
    const prevMonth = index > 0 ? RAW_MONTHS[index - 1]!.ym : null;
    const prevMissing = prevMonth ? allTx().filter((t) => t.expense && !t.expense.hasTaxInvoice && !t.expense.foreign && monthOf(t.effDate) === prevMonth) : [];

    return {
      period,
      items,
      nextCursor: null,
      summary: {
        netAgorot: ILS(expenseOf(m)),
        grossAgorot: sum(items.map((r) => r.expense.grossAgorot)),
        vatAgorot: sum(items.map((r) => r.expense.vatAgorot)),
        deductibleVatAgorot: sum(items.map((r) => r.expense.deductibleVatAgorot)),
        count: items.length,
        baselineAgorot: ILS(baseTotal),
        proratedBaselineAgorot: ILS(prorated),
        baselineMonths: baselineMonthsFor(index),
        missing: { count: missingRows.length, netAgorot: sum(missingRows.map((t) => t.expense!.netAgorot)), vatAgorot: sum(missingRows.map((t) => t.expense!.vatAgorot)) },
        missingPrevMonth: prevMonth ? { count: prevMissing.length, netAgorot: sum(prevMissing.map((t) => t.expense!.netAgorot)), month: prevMonth } : null,
      },
      categories: categoryStats(index, period),
      monthly: expenseMonths(index, 6),
    };
  }

  function invoices(query: InvoicesQuery): InvoicesResponse {
    const period = makePeriod(query.period);
    const rows = invoicesFor(query);
    const collected = collectedThisMonth();
    const oldest = overdueInvoices().sort((a, b) => (b.overdueDays ?? 0) - (a.overdueDays ?? 0))[0];
    return {
      period,
      items: rows,
      nextCursor: null,
      summary: {
        openAgorot: sum(openInvoices().map((i) => i.grossAgorot)),
        openCount: openInvoices().length,
        overdueAgorot: sum(overdueInvoices().map((i) => i.grossAgorot)),
        overdueCount: overdueInvoices().length,
        collectedThisMonthAgorot: collected.agorot,
        collectedThisMonthCount: collected.count,
        avgDaysToPay: 31,
        oldestOverdue: oldest ? { number: oldest.number, customerName: oldest.customerName, days: oldest.overdueDays ?? 0 } : null,
      },
      aging: agingStats(),
      byCustomer: customerBalances(),
      stale: null,
    };
  }

  function vat(periodParam?: string): VatResponse {
    const raw = periodParam && periodParam !== 'current' ? RAW_VAT.find((v) => v.id === periodParam) ?? currentVatRaw() : currentVatRaw();
    return {
      asOf: TODAY,
      period: toVatPeriod(raw),
      periods: RAW_VAT.map(vatPeriodRef),
      deadlines: taxEvents(60),
      missingInvoices: missingInvoices.map((x) => ({ txId: x.txId, day: x.day, merchant: x.name, category: x.category, netAgorot: ILS(x.net), vatAgorot: ILS(x.vat) })),
      trend: vatTrend(),
      history: RAW_VAT.map(toVatPeriod),
      configured: true,
    };
  }

  function suppliersResponse(): SuppliersResponse {
    const cardTotal = sum(SUPPLIERS.filter((s) => s.paymentChannel === 'card').map((s) => s.expectedMonthlyNetAgorot));
    const bankTotal = sum(SUPPLIERS.filter((s) => s.paymentChannel !== 'card').map((s) => s.expectedMonthlyNetAgorot));
    return { items: SUPPLIERS, totals: { monthlyNetAgorot: cardTotal + bankTotal, cardAgorot: cardTotal, bankAgorot: bankTotal, count: SUPPLIERS.length } };
  }

  function filterAlerts(filter: AlertFilter | undefined, includeDismissed: boolean): Alert[] {
    let list = alertsList().sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    if (!includeDismissed && filter !== 'all') list = list.filter((a) => !a.dismissedAt);
    if (filter === 'unread') list = list.filter((a) => !a.readAt && !a.dismissedAt);
    else if (filter === 'critical') list = list.filter((a) => a.severity === 'critical');
    else if (filter === 'overdue') list = list.filter((a) => a.type === 'INVOICE_OVERDUE' || a.type === 'RECEIPT_UNMATCHED');
    else if (filter === 'tax') list = list.filter((a) => a.type === 'VAT_DUE' || a.type === 'TAX_ADVANCE_DUE' || a.type === 'EXPENSE_NO_INVOICE');
    else if (filter === 'ops') list = list.filter((a) => ['CARD_CHARGE_SOON', 'CATEGORY_OVER_BASELINE', 'SUBSCRIPTION_CANCELLED', 'FORECAST_NEGATIVE', 'CONNECTION_EXPIRED', 'DATA_STALE'].includes(a.type));
    return list;
  }

  const api: Api = {
    login: (body) => {
      if (!body.email || !body.password) return Promise.reject(new ApiError('INVALID_CREDENTIALS', he.errors.INVALID_CREDENTIALS, 401));
      return later({ user: USER });
    },
    logout: () => later({ ok: true as const }),
    logoutAll: () => later({ ok: true as const }),
    me: () => later({ user: USER, appearance: settings.appearance }),
    changePassword: (body) =>
      body.next.length < 10 ? Promise.reject(new ApiError('WEAK_PASSWORD', he.errors.WEAK_PASSWORD, 400)) : later({ ok: true as const }),

    summary: (period) => later(summary(period)),
    cashflow: (query) => later(cashflow(query)),
    forecast: () => later(forecast()),

    transactions: (query) => {
      const period = makePeriod(query.period);
      const rows = transactionsFor(query);
      const inRows = rows.filter((t) => (t.amountAgorot ?? 0) > 0);
      const outRows = rows.filter((t) => (t.amountAgorot ?? 0) < 0);
      const dayMap = new Map<string, { inAgorot: number; outAgorot: number; rows: number }>();
      for (const row of rows) {
        const entry = dayMap.get(row.effDate) ?? { inAgorot: 0, outAgorot: 0, rows: 0 };
        const amount = row.amountAgorot ?? 0;
        if (amount > 0) entry.inAgorot += amount;
        else entry.outAgorot += Math.abs(amount);
        entry.rows += 1;
        dayMap.set(row.effDate, entry);
      }
      const response: TransactionsResponse = {
        period,
        items: rows,
        nextCursor: null,
        summary: {
          from: query.from ?? period.from,
          to: query.to ?? period.to,
          inAgorot: sum(inRows.map((t) => t.amountAgorot ?? 0)),
          outAgorot: sum(outRows.map((t) => Math.abs(t.amountAgorot ?? 0))),
          netAgorot: sum(rows.map((t) => t.amountAgorot ?? 0)),
          count: rows.length,
          pendingCount: rows.filter((t) => t.status === 'PENDING').length,
          unmatchedReceiptCount: inRows.filter((t) => !t.match).length,
          totalRows: allTx().length,
        },
        dayTotals: [...dayMap.entries()].map(([day, v]) => ({ day, ...v })).sort((a, b) => b.day.localeCompare(a.day)),
      };
      return later(response);
    },
    transaction: (id) => {
      const row = allTx().find((t) => t.id === id);
      if (!row) return Promise.reject(new ApiError('NOT_FOUND', he.errors.NOT_FOUND, 404));
      const detail: TransactionDetail = {
        ...row,
        matchedInvoices: (row.match?.invoiceNumbers ?? []).map((n) => INVOICES.find((i) => i.number === n)).filter((i): i is Invoice => Boolean(i)),
        matchCandidates: openInvoices()
          .map((i) => ({
            number: i.number,
            customerId: i.customerId,
            customerName: i.customerName,
            grossAgorot: i.grossAgorot,
            issueDate: i.issueDate,
            dueDate: i.dueDate,
            status: i.status,
            overdueDays: i.overdueDays,
            deltaAgorot: i.grossAgorot - (row.amountAgorot ?? 0),
          }))
          .filter((c) => Math.abs(c.deltaAgorot) <= Math.abs(row.amountAgorot ?? 0) * 0.05)
          .sort((a, b) => Math.abs(a.deltaAgorot) - Math.abs(b.deltaAgorot)),
        cycle: null,
        supplier: row.expense?.supplierId ? SUPPLIERS.find((s) => s.id === row.expense!.supplierId) ?? null : null,
      };
      return later(detail);
    },
    updateTransaction: (id, body) => {
      const current = txOverrides.get(id) ?? {};
      if (body.category !== undefined && body.category !== null) current.category = body.category;
      if (body.note !== undefined) current.note = body.note;
      txOverrides.set(id, current);
      emit({ event: 'data:changed', data: { scopes: ['transactions', 'expenses', 'summary', 'vat'] } });
      return api.transaction(id).then((item) => ({ item, affected: body.applyToMerchant ? 2 : 1 }));
    },
    matchTransaction: (id, body) => {
      const current = txOverrides.get(id) ?? {};
      current.match = body.invoiceNumber
        ? { kind: 'manual', invoiceNumbers: [body.invoiceNumber], matchedAt: SERVER_TIME, matchedBy: USER.displayName, batchId: null }
        : null;
      txOverrides.set(id, current);
      if (body.invoiceNumber) {
        const raw = RAW_INVOICES.find((i) => i.number === body.invoiceNumber);
        const index = INVOICES.findIndex((i) => i.number === body.invoiceNumber);
        if (raw && index >= 0) {
          raw.status = 'paid';
          raw.docType = 'tax_receipt';
          raw.paidAt = allTx().find((t) => t.id === id)?.effDate ?? TODAY;
          raw.matchedTxId = id;
          INVOICES[index] = toInvoice(raw);
        }
      }
      emit({ event: 'data:changed', data: { scopes: ['transactions', 'invoices', 'summary', 'vat'] } });
      return api.transaction(id);
    },

    invoices: (query) => later(invoices(query)),
    invoice: (number) => {
      try {
        return later(invoiceDetail(number));
      } catch (error) {
        return Promise.reject(error);
      }
    },
    payInvoice: (number, body) => {
      const raw = RAW_INVOICES.find((i) => i.number === number);
      const index = INVOICES.findIndex((i) => i.number === number);
      if (!raw || index < 0) return Promise.reject(new ApiError('NOT_FOUND', he.errors.NOT_FOUND, 404));
      raw.status = 'paid';
      raw.docType = 'tax_receipt';
      raw.paidAt = body.paidAt ?? TODAY;
      if (body.txId) {
        raw.matchedTxId = body.txId;
        const current = txOverrides.get(body.txId) ?? {};
        current.match = { kind: 'manual', invoiceNumbers: [number], matchedAt: SERVER_TIME, matchedBy: USER.displayName, batchId: null };
        txOverrides.set(body.txId, current);
      }
      INVOICES[index] = toInvoice(raw);
      emit({ event: 'data:changed', data: { scopes: ['invoices', 'summary', 'vat', 'transactions'] } });
      return later(invoiceDetail(number));
    },
    matchInvoice: (number, body) => {
      const raw = RAW_INVOICES.find((i) => i.number === number);
      const index = INVOICES.findIndex((i) => i.number === number);
      if (!raw || index < 0) return Promise.reject(new ApiError('NOT_FOUND', he.errors.NOT_FOUND, 404));
      raw.matchedTxId = body.txId ?? undefined;
      if (body.txId) {
        raw.status = 'paid';
        raw.docType = 'tax_receipt';
        raw.paidAt = allTx().find((t) => t.id === body.txId)?.effDate ?? TODAY;
        const current = txOverrides.get(body.txId) ?? {};
        current.match = { kind: 'manual', invoiceNumbers: [number], matchedAt: SERVER_TIME, matchedBy: USER.displayName, batchId: null };
        txOverrides.set(body.txId, current);
      }
      INVOICES[index] = toInvoice(raw);
      emit({ event: 'data:changed', data: { scopes: ['invoices', 'summary', 'transactions'] } });
      return later(invoiceDetail(number));
    },
    reminderDraft: (number) => {
      const invoice = INVOICES.find((i) => i.number === number);
      if (!invoice) return Promise.reject(new ApiError('NOT_FOUND', he.errors.NOT_FOUND, 404));
      const customer = CUSTOMER_BY_ID.get(invoice.customerId);
      const subject = `תזכורת: ${DOC_TYPE[invoice.docType].label} ${invoice.number} על סך ${fmtMoney(invoice.grossAgorot)} — LabTop`;
      const body = [
        `שלום ${invoice.customerName},`,
        '',
        `${DOC_TYPE[invoice.docType].label} ${invoice.number} מיום ${fmtDate(invoice.issueDate)} על סך ${fmtMoney(invoice.grossAgorot)} (כולל מע"מ) טרם שולמה.`,
        invoice.overdueDays ? `מועד התשלום היה ${fmtDate(invoice.dueDate)} — לפני ${invoice.overdueDays} ימים.` : `מועד התשלום: ${fmtDate(invoice.dueDate)}.`,
        '',
        'נשמח להסדרה בהקדם. אם התשלום כבר בוצע — אפשר להתעלם מההודעה.',
        '',
        'תודה,',
        'יאיר · LabTop',
        'support@labtop.co.il',
      ].join('\n');
      const draft: ReminderDraft = {
        to: customer?.email ? [customer.email] : [],
        subject,
        body,
        mailto: `mailto:${customer?.email ?? ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
        warning: customer?.email ? null : 'ללקוח אין כתובת מייל שמורה — העתק את הטיוטה ושלח ידנית',
      };
      return later(draft);
    },
    recordReminder: (number) => {
      const raw = RAW_INVOICES.find((i) => i.number === number);
      const index = INVOICES.findIndex((i) => i.number === number);
      if (!raw || index < 0) return Promise.reject(new ApiError('NOT_FOUND', he.errors.NOT_FOUND, 404));
      raw.reminders = [...(raw.reminders ?? []), TODAY];
      INVOICES[index] = toInvoice(raw);
      return later(invoiceDetail(number));
    },
    customers: () => {
      const items = RAW_CUSTOMERS.map(toCustomer);
      return later({
        items,
        totals: { openBalanceAgorot: sum(items.map((c) => c.openBalanceAgorot)), overdueAgorot: sum(items.map((c) => c.overdueAgorot)), count: items.length },
      } satisfies CustomersResponse);
    },
    customer: (id) => {
      const raw = CUSTOMER_BY_ID.get(id);
      return raw ? later(toCustomer(raw)) : Promise.reject(new ApiError('NOT_FOUND', he.errors.NOT_FOUND, 404));
    },

    vat: (period) => later(vat(period)),
    markVatPeriod: (period, body) => {
      const raw = RAW_VAT.find((v) => v.id === period);
      if (!raw) return Promise.reject(new ApiError('INVALID_VAT_PERIOD', he.errors.INVALID_VAT_PERIOD, 400));
      if (body.reset) {
        raw.status = 'open';
        raw.paidAt = undefined;
      } else if (body.paid) {
        raw.status = 'paid';
        raw.paidAt = body.paid.at;
      } else if (body.reported) {
        raw.status = 'reported';
      }
      emit({ event: 'data:changed', data: { scopes: ['vat', 'summary'] } });
      return later(toVatPeriod(raw));
    },
    taxSettings: () => later(taxSettings),
    updateTaxSettings: (body) => {
      taxSettings = { ...taxSettings, ...body, vatRates: taxSettings.vatRates, businessKind: taxSettings.businessKind };
      settings = { ...settings, tax: taxSettings };
      emit({ event: 'data:changed', data: { scopes: ['vat', 'forecast', 'summary', 'settings'] } });
      return later(taxSettings);
    },

    expenses: (query) => later(expenses(query)),
    updateExpense: (id, body) => {
      const current = txOverrides.get(id) ?? {};
      if (body.category) current.category = body.category;
      if (body.hasTaxInvoice !== undefined) current.hasTaxInvoice = body.hasTaxInvoice;
      if (body.note !== undefined) current.note = body.note;
      txOverrides.set(id, current);
      const raw = RAW_TX.find((t) => t.id === id);
      let vatPeriodId = currentVatRaw().id;
      if (body.hasTaxInvoice) {
        const missing = missingInvoices.find((x) => x.txId === id);
        const day = missing?.day ?? raw?.date ?? TODAY;
        const vatAmount = ILS(missing?.vat ?? raw?.vat ?? 0);
        vatPeriodId = vatPeriodIdOf(day);
        vatInputBonus.set(vatPeriodId, (vatInputBonus.get(vatPeriodId) ?? 0) + vatAmount);
        missingInvoices = missingInvoices.filter((x) => x.txId !== id);
      }
      const vp = RAW_VAT.find((v) => v.id === vatPeriodId);
      emit({ event: 'data:changed', data: { scopes: ['expenses', 'vat', 'summary', 'transactions'] } });
      const row = allTx().find((t) => t.id === id);
      const item: ExpenseRow = {
        txId: id,
        day: row?.effDate ?? TODAY,
        account: row?.account ?? CARD_REF,
        merchant: row?.merchant ?? '',
        description: row?.description ?? '',
        status: row?.status ?? 'BOOKED',
        expense: row?.expense ?? {
          category: 'EQUIPMENT',
          categorySource: 'manual',
          grossAgorot: 0,
          netAgorot: 0,
          vatAgorot: 0,
          hasTaxInvoice: true,
          vatShare: 1,
          deductibleVatAgorot: 0,
          foreign: false,
          paymentChannel: 'card',
          supplierId: null,
          supplierName: null,
        },
        cycleMonth: row?.cycleMonth ?? null,
        note: row?.note ?? null,
      };
      return later({
        item,
        affected: body.applyToMerchant ? 2 : 1,
        vat: { period: vatPeriodId, inputVatAgorot: vp ? toVatPeriod(vp).calc.inputVatAgorot : 0, deltaAgorot: body.hasTaxInvoice ? ILS(missingInvoices.length ? 0 : 0) + (vatInputBonus.get(vatPeriodId) ?? 0) : 0 },
      });
    },
    updateExpenseCategory: (key, body) => {
      categoryThresholds.set(key, body.thresholdPct === undefined ? 20 : body.thresholdPct);
      emit({ event: 'data:changed', data: { scopes: ['expenses', 'summary'] } });
      const settingsRow: ExpenseCategorySettings = { category: key, thresholdPct: body.thresholdPct ?? null, updatedBy: USER.displayName, updatedAt: SERVER_TIME };
      return later(settingsRow);
    },
    suppliers: () => later(suppliersResponse()),

    opsSummary: () => later(opsSummary()),
    opsCalls: (query?: OpsCallsQuery) => {
      let items = CALLS.slice();
      if (query?.status && query.status !== 'all') items = items.filter((c) => c.status === query.status);
      if (query?.type) items = items.filter((c) => c.type === query.type);
      const response: OpsCallsResponse = { source: opsSummary().source, items, nextCursor: null };
      return later(response);
    },
    opsSubscriptions: () => {
      const response: OpsSubscriptionsResponse = {
        source: opsSummary().source,
        items: SUBSCRIPTIONS.slice().sort((a, b) => Number(b.status === 'active') - Number(a.status === 'active') || b.monthlyNetAgorot - a.monthlyNetAgorot),
        mrrAgorot: opsSummary().subscriptions.mrrAgorot,
      };
      return later(response);
    },
    opsCustomers: () => {
      const response: OpsCustomersResponse = { source: opsSummary().source, items: RAW_CUSTOMERS.map(toCustomer) };
      return later(response);
    },

    accounts: () => later({ items: ACCOUNTS }),
    loans: (asOf) => later(loansFromAccounts(ACCOUNTS, asOf)),
    updateAccount: (id, body) => {
      const account = ACCOUNTS.find((a) => a.id === id);
      if (!account) return Promise.reject(new ApiError('NOT_FOUND', he.errors.NOT_FOUND, 404));
      if (body.assigned !== undefined) account.assigned = body.assigned;
      emit({ event: 'data:changed', data: { scopes: ['accounts', 'summary'] } });
      return later(account);
    },
    connections: () => later({ items: CONNECTIONS }),

    settings: () => later(settings),
    updateSettings: (body) => {
      if (body.alerts) settings = { ...settings, alerts: { ...settings.alerts, ...body.alerts } };
      if (body.appearance) settings = { ...settings, appearance: { ...settings.appearance, ...body.appearance } };
      if (body.hyp) return Promise.reject(new ApiError('NOT_IMPLEMENTED', he.settings.secretsUnsupported, 501));
      if (body.labtop) return Promise.reject(new ApiError('NOT_IMPLEMENTED', he.settings.secretsUnsupported, 501));
      emit({ event: 'data:changed', data: { scopes: ['settings'] } });
      return later(settings);
    },
    testHyp: () => later<TestIntegrationResponse>({ ok: true, latencyMs: 38, message: he.settings.hypOk(38), status: settings.integrations.hyp }),
    testLabtop: () =>
      later<TestIntegrationResponse>({ ok: false, latencyMs: null, message: he.settings.labtopFail, status: settings.integrations.labtop }),
    users: () => later({ items: [USER], max: 1 }),

    sync: (providerRefresh) => {
      emit({ event: 'sync:start', data: { runId: 42, kind: providerRefresh ? 'refresh' : 'manual', sources: ['financy', 'hyp'] } });
      setTimeout(() => {
        emit({ event: 'sync:done', data: { run: { ...syncRun, id: 42, kind: providerRefresh ? 'refresh' : 'manual', creditsUsed: providerRefresh ? 20 : 0 }, freshness } });
        emit({ event: 'data:changed', data: { scopes: ['summary', 'cashflow', 'forecast', 'transactions', 'invoices', 'vat', 'expenses', 'ops'] } });
      }, 1600);
      return later({ runId: 42, kind: providerRefresh ? ('refresh' as const) : ('manual' as const), sources: ['financy' as const, 'hyp' as const], creditsEstimate: providerRefresh ? (20 as const) : (0 as const), startedAt: SERVER_TIME });
    },
    syncStatus: () => {
      const status: SyncStatusResponse = {
        running: false,
        current: null,
        last: syncRun,
        lastSuccessful: syncRun,
        nextScheduledAt: '2026-09-06T12:12:00+03:00',
        freshness,
        sources,
        providerRefresh: { allowed: true, retryAt: null, reason: null, creditsEstimate: 20 },
      };
      return later(status);
    },

    alerts: (query?: AlertsQuery) => {
      const items = filterAlerts(query?.filter, query?.includeDismissed === '1');
      const response: AlertsResponse = { items, nextCursor: null, unreadCount: unreadCount(), openCount: activeAlerts().length };
      return later(response);
    },
    updateAlert: (id, body) => {
      const state = alertState.get(id);
      if (!state) return Promise.reject(new ApiError('NOT_FOUND', he.errors.NOT_FOUND, 404));
      if (body.read !== undefined) state.readAt = body.read ? SERVER_TIME : null;
      if (body.dismissed !== undefined) state.dismissedAt = body.dismissed ? SERVER_TIME : null;
      emit({ event: 'alerts:changed', data: { unreadCount: unreadCount(), openCount: activeAlerts().length } });
      const raw = RAW_ALERTS.find((a) => a.id === id)!;
      return later(toAlert(raw));
    },
    readAllAlerts: () => {
      for (const state of alertState.values()) if (!state.dismissedAt) state.readAt = SERVER_TIME;
      emit({ event: 'alerts:changed', data: { unreadCount: 0, openCount: activeAlerts().length } });
      return later({ updated: RAW_ALERTS.length, unreadCount: 0 });
    },

    subscribe(onEvent) {
      alertsListeners.push(onEvent);
      return () => {
        alertsListeners = alertsListeners.filter((l) => l !== onEvent);
      };
    },
    exportUrl: () => '#',
    isMock: true,
  };

  return api;
}

/** Chart labels the demo screens reuse (kept here so `monthShort` has one call site in tests). */
export const DEMO_MONTH_LABELS = RAW_MONTHS.map((m) => monthShort(m.ym));
